// src/components/pages/HomePage.js
import * as Sentry from "@sentry/react";
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  NAVBAR_HEIGHT,
  BOTTOM_NAV_BAR_HEIGHT,
} from "../../constants/constants";
import { useLoadScript } from "@react-google-maps/api";
import { Box, Button, CardActionArea, Chip, CircularProgress, Drawer, Fade, IconButton, Stack, TextField, Typography } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlaceIcon from "@mui/icons-material/Place";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import SpaIcon from "@mui/icons-material/Spa";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";

import IconShovelDig from "../../assets/icons/v2/shovel-dig.webp";
import IconShovelHover from "../../assets/icons/v2/shovel-hover.webp";
import IconTreasureBoxLocked from "../../assets/icons/v2/treasure-box-locked.webp";

import MapControls from "../MapControls";
import SuccessDialog from "../SuccessDialog";
import InstallPrompt from "../shared/InstallPromptModal";
import FindLoadingOverlay from "./FindLoadingOverlay";
import VideoAdModal from "../VideoAdModal";
import LegacyFindDialog from "./legacyFind/LegacyFindDialog";

import { useShallow } from "zustand/react/shallow";
import useUserProfileStore, {
  selectIsSubscriptionResolved,
  selectUserProfile,
  selectUserProfileId,
  selectUserSubscriptionData,
  selectUserSubscriptionTier,
} from "../../store/useUserProfileStore";
import useMobileViewportFix from "../../hooks/useMobileViewportFix";
import useUserNavigation from "../../hooks/useUserNavigation";
import useAppConfigFlags from "../../hooks/useAppConfigFlags";
import { usePwaPrompt } from "../../hooks/usePwaPrompt";

import {
  fetchNearbyGems,
  fetchLovedTimeCapsules,
  fetchUnCoverTimeCapsules,
  fetchUserDraftTimeCapsules,
} from "../../services/timeCapsuleService";
import { fetchUsersBasicInfoByIds, getLovedTimeCapsuleIds } from "../../services/profileService";
import { getAuth } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { functions, db } from "../../firebase";
import { doc, onSnapshot } from "firebase/firestore";

import useCapsuleCluster from "../../hooks/useCapsuleCluster";
import CapsuleClusterModal from "../CapsuleClusterModal";
import useMapCenterAndSearch from "../../hooks/useMapCenterAndSearch";

import useGeoPermissionPrimer from "../../hooks/useGeoPermissionPrimer";
import usePushPermissionPrimer from "../../hooks/usePushPermissionPrimer";
import PermissionPrimerDialog from "../permissions/PermissionPrimerDialog";
import PushPermissionPrimerDialog from "../permissions/PushPermissionPrimerDialog";
import PermissionDeniedOverlay from "../permissions/PermissionDeniedOverlay";
import { GOOGLE_MAP_DARK_STYLES } from "../../constants/googleMapStyles";
import { useRenderTrace } from "../../dev/renderProfiler";
import { isIOSSafari, isMobileUserAgent, isPwaInstalled } from "../../utils/platform";
import { recoverGoogleMapSurface } from "../../features/homeMapV2/HomeTimeCapsuleMapV2";
import HomeMapStage from "../../features/homeMapV2/HomeMapStage";
import useHomeMapCapsulesV2 from "../../features/homeMapV2/useHomeMapCapsulesV2";
import {
  logCapsuleImpression,
  logCapsuleEvent,
  logOfferImpression,
  logOfferEvent,
  isMeasurableCapsule,
} from "../../utils/measurement";
import {
  buildHomeMapNavigationState,
  buildHomeMapMarkerFromCapsule,
  resolveHomeMapReturnCenter,
  shouldRedirectMemberExclusiveMarkerToMembership,
} from "../../features/homeMapV2/homeMapV2Utils";
import {
  HOME_MAP_DEFAULT_CENTER as DEFAULT_CENTER,
  resolveHomeMapPassiveQuickFindCenter,
  resolveHomeMapCenterState,
} from "./homeMapCenter";
import {
  QUICK_FIND_PRESEED_REUSE_RADIUS_METERS,
  buildQuickFindDirectCategoryCacheEntry,
  buildQuickFindCategoryStatesFromCache,
  findFreshQuickFindPreseedNearCenter,
  findQuickFindPreseedInFlightNearCenter,
  hasFreshQuickFindCategoryCacheSet,
  resolveQuickFindCachedCapsules,
  resolveQuickFindCachedCapsuleIds,
  shouldSkipRouteQuickFindPreseed,
} from "../../utils/quickFindPreseed";
import { askGroundedDiscover } from "../../services/discoverService";
import { seedQuickFindCategories } from "../../services/quickFindSeedService";
import { recordFindHistory } from "../../services/historyService";
import { fetchNearbyCampaignOffers, getCampaignOfferClaimPath } from "../../services/offersService";
import { hasInsiderAccess } from "../../utils/insiderAccess";
import {
  formatNearbyDistance,
  getNearbyDiscoverySheetTitle,
  getNearbyContactShortcutLabel,
  getNearbyLovedShortcutLabel,
  getNearbyGemKindLabel,
  getNearbyGemThumbnailFallbackKind,
  getNearbyGemThumbnailUrl,
  getNearbyShortcutState,
  groupNearbyGemsBySection,
  mergeLovedNearbyGems,
  resolveNearbyDiscoveryResults,
  shouldLoadNearbyDiscovery,
} from "../../utils/nearbyDiscovery";
import { buildDiscoveryRowLabels, isGatedMemberExclusive } from "../../utils/discoveryRowLabels";
import { getSponsoredBusinessMarkerMap } from "../../services/sponsoredAvatarService";
import {
  buildLegacyFindNoResultsMessage,
  isLegacyFindNoResults,
} from "../../utils/legacyFindResults";
import { buildQuickFindHistoryRecord } from "../../utils/quickFindHistory";
import {
  getUsableMapLatLng,
  getRouteDestination,
  distanceMeters,
  buildOfferCenterKey,
  snapCenterToGrid,
} from "./homeMapGeo";
import { isQuickFindReturnState } from "./quickFindReturnState";

const MAP_LIBRARIES = ["places"];
const SIGNIN_REDIRECT_KEY = "postAuthRedirectTo";
const HIDDEN_MAP_CAPSULE_IDS_KEY = "inssa:hidden-map-capsule-ids";
const WALK_ROUTE_TIMEOUT_MS = 12000;
const PRIVATE_NEARBY_NOTIFICATION_RADIUS_METERS = 100000;
const NEARBY_GEMS_RADIUS_METERS = 15000;
// Hard cap on how long the map center may stay unresolved before we fall back to
// the app default center. Bounds the worst case where a slow GPS prompt or a
// slow center cascade would otherwise leave effectiveCenter null — which blocks
// the map render and the nearby-discovery (gems/offers) fetch. A real center
// still takes over once GPS/GeoIP lands.
const HOME_CENTER_WAIT_CAP_MS = 6000;
const HOME_MAP_CATEGORY_FILTERS_ENABLED = false;
const FIND_AD_RESULTS_GAP_PX = 12;
const FIND_AD_RESERVED_RESULTS_SPACE = `calc(min(56.25vw, 360px) + ${FIND_AD_RESULTS_GAP_PX}px)`;
const DEBUG_HOME_MAP_SHOVEL =
  String(
    process.env.REACT_APP_DEBUG_HOME_MAP_SHOVEL ||
      (typeof window !== "undefined"
        ? window.localStorage?.getItem("debugHomeMapShovel")
        : "") ||
      ""
  ).toLowerCase() === "true";

function recordFindClientEvent(phase, details = {}) {
  const payload = {
    phase: String(phase || "").trim(),
    at: Date.now(),
    ...details,
  };
  try {
    Sentry.addBreadcrumb({
      category: "find-ai",
      level: "info",
      message: payload.phase,
      data: payload,
    });
  } catch {}
  try {
    const key = "inssa:find-ai-debug-events:v1";
    const previous = JSON.parse(window.sessionStorage.getItem(key) || "[]");
    const next = [...(Array.isArray(previous) ? previous : []), payload].slice(-20);
    window.sessionStorage.setItem(key, JSON.stringify(next));
  } catch {}
}

function getOfferTypeLabel(offer = {}) {
  return offer?.campaignType === "coupon" ? "Coupon" : "Raffle";
}

function getOfferClaimPath(offer = {}) {
  return getCampaignOfferClaimPath(offer);
}

function getNearbyGemChipSx(gemKind) {
  return (theme) => {
    const base = {
      height: 22,
      fontWeight: 800,
      borderRadius: 1,
      "& .MuiChip-label": { px: 0.9 },
    };

    if (gemKind === "sponsored") {
      return {
        ...base,
        color: theme.palette.mode === "dark" ? "#E9D8FF" : theme.colors.inssa,
        backgroundColor: alpha(theme.colors.lightPurple, theme.palette.mode === "dark" ? 0.28 : 0.18),
        border: `1px solid ${alpha(theme.colors.lightPurple, theme.palette.mode === "dark" ? 0.42 : 0.32)}`,
      };
    }

    if (gemKind === "memberExclusive") {
      return {
        ...base,
        color: theme.palette.mode === "dark" ? "#C8F7EA" : "#0B5E50",
        backgroundColor: alpha(theme.colors.green, theme.palette.mode === "dark" ? 0.2 : 0.14),
        border: `1px solid ${alpha(theme.colors.green, theme.palette.mode === "dark" ? 0.36 : 0.28)}`,
      };
    }

    return {
      ...base,
      color: theme.palette.mode === "dark" ? "#FFE9B0" : "#6F4A00",
      backgroundColor: alpha(theme.colors.amber, theme.palette.mode === "dark" ? 0.22 : 0.16),
      border: `1px solid ${alpha(theme.colors.amber, theme.palette.mode === "dark" ? 0.34 : 0.28)}`,
    };
  };
}

const DEFAULT_MARKER_VISIBILITY = {
  received: true,
  buried: true,
  personal: true,
  drafts: true,
  public: true,
};
const NEARBY_DRAFTS_RADIUS_METERS = 1500;
const UNCOVER_CATEGORY_OPTIONS = [
  { key: "see", label: "See", icon: PlaceIcon },
  { key: "eat", label: "Eat", icon: RestaurantIcon },
  { key: "drink", label: "Drink", icon: LocalBarIcon },
  { key: "do", label: "Do", icon: SportsEsportsIcon },
  { key: "shop", label: "Shop", icon: ShoppingBagIcon },
  { key: "relax", label: "Relax", icon: SpaIcon },
];
const UNCOVER_DEFAULT_CATEGORIES = UNCOVER_CATEGORY_OPTIONS.map((item) => item.key);
const QUICK_FIND_CATEGORY_KEYS = ["eat", "drink", "fun"];
const QUICK_FIND_PRESEED_KEY = "eat-drink-fun";
const QUICK_FIND_PRESEED_CACHE_KEY = "inssa:quick-find-preseed-cache:v7";
const QUICK_FIND_PRESEED_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const QUICK_FIND_MIN_READY_COUNT = 3;
const QUICK_FIND_RECENTER_CHIP_MIN_DISTANCE_METERS = 800;

function buildIdleQuickFindCategoryStates() {
  return QUICK_FIND_CATEGORY_KEYS.reduce((states, key) => {
    states[key] = { phase: "idle", count: 0, capsuleIds: [] };
    return states;
  }, {});
}

const wobble = keyframes`
  0%   { transform: translate(-50%, -100%) rotate(0deg); }
  25%  { transform: translate(-50%, -100%) rotate(-5deg); }
  50%  { transform: translate(-50%, -100%) rotate(0deg); }
  75%  { transform: translate(-50%, -100%) rotate(5deg); }
  100% { transform: translate(-50%, -100%) rotate(0deg); }
`;
const uncoverGlow = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(86,195,114,0.32); }
  70%  { box-shadow: 0 0 0 14px rgba(86,195,114,0); }
  100% { box-shadow: 0 0 0 0 rgba(86,195,114,0); }
`;
const uncoverMapPulse = keyframes`
  0%   { transform: scale(0.98); opacity: 0.18; }
  60%  { transform: scale(1.02); opacity: 0.28; }
  100% { transform: scale(0.98); opacity: 0.18; }
`;
const uncoverRadarExpand = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.04);
    opacity: 0.48;
  }
  75% {
    opacity: 0.2;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.18);
    opacity: 0;
  }
`;
const uncoverRadarRotate = keyframes`
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
`;
const findVoiceRipple = keyframes`
  0% { transform: scale(0.72); opacity: 0.72; }
  70% { opacity: 0.18; }
  100% { transform: scale(1.16); opacity: 0; }
`;
const findVoiceSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;
const nearbyGemButtonBounce = keyframes`
  0% { transform: translateY(0) scale(1); }
  18% { transform: translateY(-9px) scale(1.04); }
  36% { transform: translateY(0) scale(0.98); }
  54% { transform: translateY(-5px) scale(1.02); }
  72% { transform: translateY(0) scale(1); }
  100% { transform: translateY(0) scale(1); }
`;
const FIND_VOICE_AUTO_SUBMIT_DELAY_MS = 1800;
const UNCOVER_DEVICE_ID_KEY = "inssa:uncover:device-id";
const UNCOVER_CATEGORY_PREFS_KEY_PREFIX = "inssa:uncover:categories:v1";
const MARKER_VISIBILITY_PREFS_KEY_PREFIX = "inssa:home:marker-visibility:v1";
const CLUSTER_RETURN_STATE_KEY = "inssa:home:cluster-return:v1";
const CLUSTER_RETURN_STATE_TTL_MS = 20 * 60 * 1000;
function normalizeUncoverCategories(categories) {
  const allowedKeys = new Set(UNCOVER_DEFAULT_CATEGORIES);
  if (!Array.isArray(categories)) return UNCOVER_DEFAULT_CATEGORIES;
  const selected = Array.from(
    new Set(
      categories
        .map((value) => String(value || "").trim().toLowerCase())
        .filter((value) => allowedKeys.has(value))
    )
  );
  if (!selected.length) return UNCOVER_DEFAULT_CATEGORIES;
  return UNCOVER_DEFAULT_CATEGORIES.filter((key) => selected.includes(key));
}

function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function buildUncoverCategoriesPrefsKey(userId) {
  const normalizedUserId = String(userId || "").trim();
  return `${UNCOVER_CATEGORY_PREFS_KEY_PREFIX}:${normalizedUserId || "anon"}`;
}

function loadSavedUncoverCategories(userId) {
  if (typeof window === "undefined") return UNCOVER_DEFAULT_CATEGORIES;
  try {
    const raw = window.localStorage.getItem(buildUncoverCategoriesPrefsKey(userId));
    if (!raw) return UNCOVER_DEFAULT_CATEGORIES;
    const parsed = JSON.parse(raw);
    return normalizeUncoverCategories(parsed);
  } catch {
    return UNCOVER_DEFAULT_CATEGORIES;
  }
}

function saveUncoverCategoriesPreference(userId, categories) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      buildUncoverCategoriesPrefsKey(userId),
      JSON.stringify(normalizeUncoverCategories(categories))
    );
  } catch {
    // ignore localStorage failures
  }
}

function buildMarkerVisibilityPrefsKey(userId) {
  const normalizedUserId = String(userId || "").trim();
  return `${MARKER_VISIBILITY_PREFS_KEY_PREFIX}:${normalizedUserId || "anon"}`;
}

function normalizeMarkerVisibility(visibility = {}) {
  const value = visibility && typeof visibility === "object" ? visibility : {};
  return {
    received: value.received !== false,
    buried: (value.buried ?? value.sent) !== false,
    personal: value.personal !== false,
    drafts: value.drafts !== false,
    public: value.public !== false,
  };
}

function loadSavedMarkerVisibility(userId) {
  if (typeof window === "undefined") return { ...DEFAULT_MARKER_VISIBILITY };
  if (!HOME_MAP_CATEGORY_FILTERS_ENABLED) return { ...DEFAULT_MARKER_VISIBILITY };
  try {
    const raw = window.localStorage.getItem(buildMarkerVisibilityPrefsKey(userId));
    if (!raw) return { ...DEFAULT_MARKER_VISIBILITY };
    return normalizeMarkerVisibility(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_MARKER_VISIBILITY };
  }
}

function saveMarkerVisibilityPreference(userId, visibility) {
  if (typeof window === "undefined") return;
  if (!HOME_MAP_CATEGORY_FILTERS_ENABLED) {
    try {
      window.localStorage.setItem(
        buildMarkerVisibilityPrefsKey(userId),
        JSON.stringify({ ...DEFAULT_MARKER_VISIBILITY })
      );
    } catch {
      // ignore localStorage failures
    }
    return;
  }
  try {
    window.localStorage.setItem(
      buildMarkerVisibilityPrefsKey(userId),
      JSON.stringify(normalizeMarkerVisibility(visibility))
    );
  } catch {
    // ignore localStorage failures
  }
}

function loadQuickFindPreseedCache() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(QUICK_FIND_PRESEED_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function saveQuickFindPreseedCache(cache = {}) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(QUICK_FIND_PRESEED_CACHE_KEY, JSON.stringify(cache || {}));
  } catch {
    // ignore localStorage failures
  }
}

function buildQuickFindPreseedLocationKey(center, placeId = "") {
  const normalizedPlaceId = String(placeId || "").trim();
  if (normalizedPlaceId) return `place:${normalizedPlaceId}`;
  const lat = Number(center?.lat);
  const lng = Number(center?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
  return `geo:${lat.toFixed(3)},${lng.toFixed(3)}`;
}

function buildQuickFindPreseedCacheKey(center, categoryKey, placeId = "") {
  const locationKey = buildQuickFindPreseedLocationKey(center, placeId);
  const normalizedCategoryKey = String(categoryKey || "").trim().toLowerCase();
  if (!locationKey || !normalizedCategoryKey) return "";
  return `${locationKey}:${normalizedCategoryKey}`;
}

function mergeCapsulesById(previousCapsules = [], nextCapsules = []) {
  const byId = new Map();
  [...(Array.isArray(previousCapsules) ? previousCapsules : []), ...(Array.isArray(nextCapsules) ? nextCapsules : [])]
    .forEach((capsule) => {
      const id = String(capsule?.id || "").trim();
      if (!id) return;
      byId.set(id, capsule);
    });
  return Array.from(byId.values());
}

function loadClusterReturnState() {
  if (typeof window === "undefined") return null;
  try {
    const raw = String(window.sessionStorage.getItem(CLUSTER_RETURN_STATE_KEY) || "").trim();
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.createdAt || 0);
    if (!Number.isFinite(createdAt) || Date.now() - createdAt > CLUSTER_RETURN_STATE_TTL_MS) {
      window.sessionStorage.removeItem(CLUSTER_RETURN_STATE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveClusterReturnState(payload) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      CLUSTER_RETURN_STATE_KEY,
      JSON.stringify({
        ...payload,
        createdAt: Date.now(),
      })
    );
  } catch {
    // ignore sessionStorage failures
  }
}

function clearClusterReturnState() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(CLUSTER_RETURN_STATE_KEY);
  } catch {
    // ignore sessionStorage failures
  }
}

function getOrCreateUncoverDeviceId() {
  if (typeof window === "undefined") return "";
  try {
    const existing = String(window.localStorage.getItem(UNCOVER_DEVICE_ID_KEY) || "").trim();
    if (existing) return existing;
    const nextId =
      window?.crypto && typeof window.crypto.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(UNCOVER_DEVICE_ID_KEY, nextId);
    return nextId;
  } catch {
    return "";
  }
}

function getWalkRouteErrorMessage(status) {
  switch (String(status || "").toUpperCase()) {
    case "ZERO_RESULTS":
      return "No walkable path was found from your current location to this capsule yet.";
    case "NOT_FOUND":
    case "MISSING_DATA":
      return "We couldn't confirm your location well enough to start walking directions.";
    case "OVER_QUERY_LIMIT":
    case "UNKNOWN_ERROR":
      return "Directions are temporarily busy. Please try again in a moment.";
    case "ROUTE_TIMEOUT":
      return "Directions took too long to respond. Please try again.";
    case "REQUEST_DENIED":
      return "Directions are currently unavailable on this device.";
    default:
      return "We couldn't start walking directions right now.";
  }
}

function normalizeClusterUiState(uiState = null) {
  if (!uiState || typeof uiState !== "object") {
    return {
      tab: 0,
      viewMode: "discover",
      hasManuallySelectedFilter: false,
      scrollTop: 0,
    };
  }
  const normalizedViewMode =
    uiState.viewMode === "drafts" || uiState.viewMode === "insiders"
      ? uiState.viewMode
      : "discover";
  return {
    tab: Number.isFinite(Number(uiState.tab)) ? Number(uiState.tab) : 0,
    viewMode: normalizedViewMode,
    hasManuallySelectedFilter: uiState.hasManuallySelectedFilter === true,
    scrollTop: Math.max(0, Math.round(Number(uiState.scrollTop || 0))),
  };
}

function areClusterUiStatesEqual(a, b) {
  const left = normalizeClusterUiState(a);
  const right = normalizeClusterUiState(b);
  return (
    left.tab === right.tab &&
    left.viewMode === right.viewMode &&
    left.hasManuallySelectedFilter === right.hasManuallySelectedFilter &&
    left.scrollTop === right.scrollTop
  );
}

const DraftSavedDialogTransition = React.forwardRef(function DraftSavedDialogTransition(
  props,
  ref
) {
  return <Fade ref={ref} timeout={{ enter: 220, exit: 160 }} {...props} />;
});

const MapRefreshOverlay = React.memo(function MapRefreshOverlay({
  open,
  isAndroidChrome = false,
}) {
  const [showHeavyRadarLayers, setShowHeavyRadarLayers] = useState(!isAndroidChrome);

  useEffect(() => {
    if (!open) {
      setShowHeavyRadarLayers(!isAndroidChrome);
      return;
    }
    if (!isAndroidChrome) {
      setShowHeavyRadarLayers(true);
    } else {
      setShowHeavyRadarLayers(false);
    }

    const timers = [];
    if (isAndroidChrome) {
      timers.push(
        setTimeout(() => {
          setShowHeavyRadarLayers(true);
        }, 180)
      );
    }

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [isAndroidChrome, open]);

  if (!open) return null;

  const radarFieldSize = isAndroidChrome ? "min(110vmax, 1100px)" : "150vmax";
  const backdropContentBlur = isAndroidChrome ? "none" : "blur(3px)";
  const sweepBlur = isAndroidChrome ? "none" : "blur(1px)";

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 19,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <Box
        sx={(theme) => ({
          position: "absolute",
          inset: 0,
          background: `radial-gradient(circle at 50% 50%, ${alpha(theme.colors.green, 0.04)}, ${alpha(theme.colors.blue, 0.08)} 48%, ${alpha(theme.palette.common.black, 0.08)} 100%)`,
          backdropFilter: backdropContentBlur,
          WebkitBackdropFilter: backdropContentBlur,
          animation: `${uncoverMapPulse} 2s ease-in-out infinite`,
        })}
      />

      {showHeavyRadarLayers &&
        [0, 1].map((ring) => (
          <Box
            key={`map-refresh-ring-${ring}`}
            sx={(theme) => ({
              position: "absolute",
              left: "50%",
              top: "50%",
              width: radarFieldSize,
              height: radarFieldSize,
              borderRadius: "50%",
              border: `1.2px solid ${alpha(theme.colors.green, isAndroidChrome ? 0.18 : 0.28)}`,
              boxShadow: `0 0 22px ${alpha(theme.colors.green, 0.12)}`,
              transform: "translate(-50%, -50%) scale(0.04)",
              animation: `${uncoverRadarExpand} 3.2s ease-out infinite`,
              animationDelay: `${ring * 1.1}s`,
            })}
          />
        ))}

      {showHeavyRadarLayers ? (
        <Box
          sx={(theme) => ({
            position: "absolute",
            left: "50%",
            top: "50%",
            width: radarFieldSize,
            height: radarFieldSize,
            borderRadius: "50%",
            background: `conic-gradient(from 0deg, transparent 0deg, ${alpha(theme.colors.green, isAndroidChrome ? 0.14 : 0.2)} 32deg, transparent 74deg)`,
            transform: "translate(-50%, -50%)",
            animation: `${uncoverRadarRotate} 4.8s linear infinite`,
            filter: sweepBlur,
            opacity: isAndroidChrome ? 0.5 : 0.66,
          })}
        />
      ) : null}

      <Box
        sx={(theme) => ({
          position: "absolute",
          left: "50%",
          top: "50%",
          width: 58,
          height: 58,
          transform: "translate(-50%, -50%)",
          borderRadius: "999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `2px solid ${alpha(theme.colors.green, 0.38)}`,
          backgroundColor: alpha(theme.colors.green, 0.08),
          animation: `${uncoverGlow} 1.8s ease-out infinite`,
        })}
      >
        <CircularProgress
          size={24}
          thickness={5}
          sx={(theme) => ({ color: theme.colors.green })}
        />
      </Box>

    </Box>
  );
});

const FindAskDialog = React.memo(function FindAskDialog({
  open,
  prompt,
  voiceStatus,
  error,
  destinationCandidates = [],
  isListening,
  isSubmitting,
  canUseSpeech,
  onClose,
  onPromptChange,
  onSubmit,
  onVoice,
  onConfirmDestination,
}) {
  const isVoiceAutoSubmitPending =
    isListening || String(voiceStatus || "").trim().startsWith("Got it:");
  const showVoiceMode = isVoiceAutoSubmitPending;
  const voiceTranscript = String(prompt || "").trim();

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (isSubmitting) return;
        onClose();
      }}
      fullWidth
      maxWidth="xs"
    >
      <Box component="form" onSubmit={onSubmit}>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            pr: 1.5,
          }}
        >
          <Box component="span">Ask anything</Box>
          <IconButton
            aria-label="Close find"
            onClick={onClose}
            disabled={isSubmitting}
            size="small"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
              backgroundColor: alpha(theme.palette.text.primary, 0.06),
              "&:hover": {
                color: theme.palette.text.primary,
                backgroundColor: alpha(theme.palette.text.primary, 0.1),
              },
            })}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
            {isSubmitting ? (
              <Box sx={{ width: "100%", textAlign: "center", py: 1.5 }}>
                <Box
                  sx={(theme) => ({
                    position: "relative",
                    width: 132,
                    height: 132,
                    mx: "auto",
                    mb: 1.25,
                    borderRadius: "50%",
                    overflow: "hidden",
                    background: `radial-gradient(circle, ${alpha(theme.colors.green, 0.16)}, transparent 64%)`,
                  })}
                >
                  {[0, 1, 2].map((ring) => (
                    <Box
                      key={`find-radar-ring-${ring}`}
                      sx={(theme) => ({
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: 130,
                        height: 130,
                        borderRadius: "50%",
                        border: `1px solid ${alpha(theme.colors.green, 0.34)}`,
                        transform: "translate(-50%, -50%) scale(0.04)",
                        animation: `${uncoverRadarExpand} 3s ease-out infinite`,
                        animationDelay: `${ring * 0.75}s`,
                      })}
                    />
                  ))}
                  <Box
                    sx={(theme) => ({
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      width: 128,
                      height: 128,
                      borderRadius: "50%",
                      background: `conic-gradient(from 0deg, transparent 0deg, ${alpha(theme.colors.green, 0.24)} 42deg, transparent 82deg)`,
                      transform: "translate(-50%, -50%)",
                      animation: `${uncoverRadarRotate} 3.2s linear infinite`,
                    })}
                  />
                  <Box
                    sx={(theme) => ({
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      width: 48,
                      height: 48,
                      transform: "translate(-50%, -50%)",
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      backgroundColor: alpha(theme.colors.green, 0.14),
                      border: `2px solid ${alpha(theme.colors.green, 0.4)}`,
                      animation: `${uncoverGlow} 1.8s ease-out infinite`,
                    })}
                  >
                    <CircularProgress size={22} thickness={5} sx={(theme) => ({ color: theme.colors.green })} />
                  </Box>
                </Box>
                <Typography sx={{ fontWeight: 800 }}>Uncovering nearby time capsules...</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Finding the best matches for your map.
                </Typography>
              </Box>
            ) : (
              <>
                {voiceStatus && !showVoiceMode ? (
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      color: isListening ? theme.palette.success.main : theme.palette.text.secondary,
                      fontWeight: 700,
                      textAlign: "center",
                    })}
                  >
                    {voiceStatus}
                  </Typography>
                ) : null}
              </>
            )}

            {showVoiceMode ? (
              <Box
                sx={(theme) => ({
                  borderRadius: 1,
                  px: 1.4,
                  py: 1.15,
                  backgroundColor: alpha(theme.palette.text.primary, 0.06),
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                })}
              >
                <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800 }}>
                  {voiceTranscript ? "Searching for" : "Listening"}
                </Typography>
                <Typography sx={{ fontWeight: 900, lineHeight: 1.2, color: voiceTranscript ? "text.primary" : "text.secondary" }}>
                  {voiceTranscript || "Say what you want to find"}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 0.75, alignItems: "stretch" }}>
                <TextField
                  label="Nearby or abroad"
                  placeholder="Try food nearby or cafes in Rome"
                  inputProps={{ "aria-label": "Ask nearby or abroad" }}
                  value={prompt}
                  onChange={(event) => onPromptChange(event.target.value)}
                  disabled={isSubmitting}
                  size="small"
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || !prompt.trim()}
                  sx={(theme) => ({
                    minWidth: 82,
                    px: 1.75,
                    textTransform: "none",
                    borderRadius: "12px",
                    fontWeight: 800,
                    backgroundColor: theme.colors.green,
                    color: theme.colors.white,
                    "&:hover": {
                      backgroundColor: theme.colors.green,
                    },
                  })}
                >
                  Find
                </Button>
              </Box>
            )}

            {destinationCandidates.length ? (
              <Box
                sx={(theme) => ({
                  borderRadius: 1,
                  px: 1.25,
                  py: 1,
                  backgroundColor: alpha(theme.colors.blue, 0.12),
                  border: `1px solid ${alpha(theme.colors.blue, 0.26)}`,
                })}
              >
                <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 800, mb: 0.75 }}>
                  Did you mean?
                </Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  {destinationCandidates.map((candidate) => (
                    <Chip
                      key={`${candidate.placeId || candidate.label}-${candidate.center?.lat || ""}-${candidate.center?.lng || ""}`}
                      label={candidate.label}
                      clickable
                      disabled={isSubmitting}
                      onClick={() => onConfirmDestination?.(candidate)}
                      sx={{ maxWidth: "100%" }}
                    />
                  ))}
                </Stack>
              </Box>
            ) : null}

            <Box sx={{ display: "flex", justifyContent: "center", pt: 2.5, pb: 0.75 }}>
            <IconButton
              type="button"
              aria-label={isListening ? "Stop listening" : "Use voice to find"}
              onClick={onVoice}
              disabled={!canUseSpeech || isSubmitting}
              sx={(theme) => ({
                position: "relative",
                width: 92,
                height: 92,
                overflow: "hidden",
                borderRadius: "999px",
                color: theme.colors.white,
                border: `3px solid ${alpha(isListening ? theme.palette.error.main : theme.colors.blue, 0.78)}`,
                backgroundColor: isListening ? theme.palette.error.main : theme.colors.blue,
                boxShadow: isListening
                  ? `0 14px 34px ${alpha(theme.palette.error.main, 0.3)}`
                  : `0 16px 42px ${alpha(theme.colors.blue, 0.32)}`,
                "&::before": isListening
                  ? {
                      content: '""',
                      position: "absolute",
                      inset: 8,
                      borderRadius: "50%",
                      border: `2px solid ${alpha(theme.colors.white, 0.42)}`,
                      animation: `${findVoiceRipple} 1.7s ease-out infinite`,
                    }
                  : {},
                "&::after": isListening
                  ? {
                      content: '""',
                      position: "absolute",
                      inset: 11,
                      borderRadius: "50%",
                      background: `conic-gradient(from 0deg, transparent 0deg, ${alpha(theme.colors.white, 0.42)} 72deg, transparent 122deg)`,
                      animation: `${findVoiceSpin} 1.5s linear infinite`,
                    }
                  : {},
                "&:hover": {
                  backgroundColor: isListening ? theme.palette.error.main : theme.colors.blue,
                  transform: "scale(1.03)",
                },
                "&.Mui-disabled": {
                  color: alpha(theme.colors.white, 0.46),
                  borderColor: alpha(theme.colors.white, 0.12),
                  backgroundColor: alpha(theme.colors.blue, 0.12),
                },
              })}
            >
              <Box
                component="span"
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {isListening ? <StopRoundedIcon sx={{ fontSize: 40 }} /> : <MicRoundedIcon sx={{ fontSize: 46 }} />}
              </Box>
            </IconButton>
            </Box>

            {!canUseSpeech ? (
              <Typography variant="caption" color="text.secondary">
                Voice input is not available in this browser.
              </Typography>
            ) : null}

            {error ? (
              <Typography variant="caption" color="error" sx={{ display: "block", textAlign: "center" }}>
                {error}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
});

class RecoverableClusterModalBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Find results render failed:", error, info);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Dialog open={Boolean(this.props.open)} onClose={this.props.onClose} fullWidth maxWidth="xs">
        <DialogTitle>Couldn't Open Results</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            We found results, but couldn't display them cleanly. Try the search again or adjust the wording.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={this.props.onClose}
            sx={{ textTransform: "none", borderRadius: "999px" }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              this.setState({ hasError: false });
              this.props.onRetry?.();
            }}
            sx={{ textTransform: "none", borderRadius: "999px" }}
          >
            Try again
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const HomeActionBar = React.memo(function HomeActionBar({
  isNavigating,
  travelTime,
  showDriveButton,
  isStreetViewActive,
  nearbyOffersCount = 0,
  nearbyGemsCount = 0,
  nearbyDiscoveryDisplayCount = nearbyOffersCount + nearbyGemsCount,
  onDiscover,
  onNearbyDiscovery,
  onBury,
  onCancelNavigation,
  onDriveHere,
}) {
  const showNearbyDiscoveryButton = nearbyDiscoveryDisplayCount > 0;
  const nearbyDiscoveryBadgeText =
    nearbyDiscoveryDisplayCount > 99 ? "99+" : String(nearbyDiscoveryDisplayCount);
  const nearbyDiscoveryLabel = showNearbyDiscoveryButton
    ? `${nearbyDiscoveryBadgeText} nearby discoveries`
    : "";

  const actionBarBottomOffset = {
    xs: `calc(${BOTTOM_NAV_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 38px)`,
    md: `calc(${BOTTOM_NAV_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 12px)`,
  };

  if (!isNavigating && !isStreetViewActive) {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: actionBarBottomOffset,
          left: { xs: 16, md: "50%" },
          right: { xs: 16, md: "auto" },
          transform: { xs: "none", md: "translateX(-50%)" },
          width: { xs: "auto", md: "min(640px, calc(100vw - 48px))" },
          zIndex: 2,
          display: "grid",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: showNearbyDiscoveryButton
              ? { xs: "1fr 64px 1fr", sm: "1fr 68px 1fr" }
              : "1fr 0px 1fr",
            alignItems: "center",
            columnGap: showNearbyDiscoveryButton
              ? { xs: 1, sm: 2 }
              : { xs: 0.5, sm: 1 },
            transition: "grid-template-columns 220ms ease, column-gap 220ms ease",
          }}
        >
          <Button
            onClick={onDiscover}
            variant="contained"
            sx={(theme) => ({
              backgroundColor: theme.colors.green,
              color: theme.colors.white,
              height: showNearbyDiscoveryButton ? 64 : "auto",
              p: showNearbyDiscoveryButton ? "0 20px" : "10px 20px",
              fontSize: "1.25rem",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "50px",
              boxShadow: "none",
              border: `4px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.72)"}`,
              "&:hover": { boxShadow: "none", borderColor: theme.colors.white },
              minWidth: 0,
              transition: "height 220ms ease, padding 220ms ease, min-width 220ms ease, border-color 180ms ease",
            })}
          >
            Find
          </Button>

          <Button
            sx={(theme) => ({
              backgroundColor: theme.colors.lightPurple,
              color: theme.palette.primary.white,
              height: showNearbyDiscoveryButton ? 64 : "auto",
              p: showNearbyDiscoveryButton ? "0 20px" : "10px 20px",
              fontSize: "1.25rem",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "50px",
              boxShadow: "none",
              border: `4px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.72)"}`,
              "&:hover": { boxShadow: "none", borderColor: theme.colors.white },
              minWidth: 0,
              gridColumn: 3,
              transition: "height 220ms ease, padding 220ms ease, min-width 220ms ease, border-color 180ms ease",
            })}
            onClick={onBury}
            variant="contained"
          >
            Bury
          </Button>

          {showNearbyDiscoveryButton ? (
            <Button
              onClick={onNearbyDiscovery}
              variant="contained"
              aria-label={nearbyDiscoveryLabel}
              sx={(theme) => ({
                gridColumn: 2,
                gridRow: 1,
                justifySelf: "center",
                width: 64,
                minWidth: 64,
                height: 64,
                p: 0,
                backgroundColor: theme.colors.blue,
                color: theme.palette.primary.white,
                textTransform: "none",
                borderRadius: "50%",
                boxShadow: "none",
                animation: `${nearbyGemButtonBounce} 1200ms ease-out 420ms 1 both`,
                zIndex: 1,
                "&:hover": {
                  boxShadow: "none",
                  backgroundColor: theme.colors.blue,
                  transform: "scale(1.04)",
                  borderColor: theme.colors.white,
                },
                border: `4px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.72)"}`,
                transition: "transform 180ms ease, border-color 180ms ease",
              })}
            >
              <DiamondOutlinedIcon sx={{ fontSize: 32 }} />
              <Box
                component="span"
                sx={(theme) => ({
                  position: "absolute",
                  top: -12,
                  right: -14,
                  minWidth: nearbyDiscoveryBadgeText.length === 1 ? 30 : 38,
                  height: 30,
                  px: nearbyDiscoveryBadgeText.length === 1 ? 0 : "8px",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "999px",
                  border: `2px solid ${theme.palette.common.white}`,
                  backgroundColor: theme.colors.green,
                  color: theme.colors.white,
                  fontSize: 13,
                  fontWeight: 950,
                  lineHeight: 1,
                  letterSpacing: 0,
                  whiteSpace: "nowrap",
                  maxWidth: "none",
                  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.34)}`,
                })}
              >
                {nearbyDiscoveryBadgeText}
              </Box>
            </Button>
          ) : null}
        </Box>
      </Box>
    );
  }

  if (!isNavigating) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: actionBarBottomOffset,
        left: { xs: 16, md: "50%" },
        right: { xs: 16, md: "auto" },
        transform: { xs: "none", md: "translateX(-50%)" },
        width: { xs: "auto", md: "min(640px, calc(100vw - 48px))" },
        zIndex: 2,
      }}
    >
      <Box sx={{ display: "flex", gap: 1.25 }}>
        <Button
          onClick={onCancelNavigation}
          variant="contained"
          sx={(theme) => ({
            flex: 1,
            backgroundColor: theme.colors.red,
            color: theme.palette.primary.white,
            p: "10px 20px",
            textTransform: "none",
            borderRadius: "50px",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
            border: "2px solid white",
          })}
        >
          {travelTime
            ? `End Walk · ${travelTime}`
            : "End Walk"}
        </Button>
        {showDriveButton ? (
          <Button
            onClick={onDriveHere}
            variant="contained"
            sx={(theme) => ({
              flex: 1,
              backgroundColor: theme.colors.blue,
              color: theme.palette.primary.white,
              p: "10px 20px",
              textTransform: "none",
              borderRadius: "50px",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
              border: "2px solid white",
            })}
          >
            Drive Here
          </Button>
        ) : null}
      </Box>
    </Box>
  );
});

export default function HomePage() {
  useMobileViewportFix();
  // SEO for "/" is applied once by GlobalSeoRouteManager in App.js; no per-page hook needed here.
  const theme = useTheme();
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const { geminiFindEnabled } = useAppConfigFlags();
  const distanceLocale = useMemo(
    () => navigator.languages?.[0] || navigator.language || "en-US",
    []
  );
  const [usersList, setUsersList] = useState(null);

  // Auth & user profile
  const auth = getAuth();
  const user = auth.currentUser;
  const currentUserId = user?.uid || "";
  const {
    userProfile,
    userProfileId,
    subscriptionData,
    subscriptionTier,
    subscriptionResolved,
  } = useUserProfileStore(
    useShallow((state) => ({
      userProfile: selectUserProfile(state),
      userProfileId: selectUserProfileId(state),
      subscriptionData: selectUserSubscriptionData(state),
      subscriptionTier: selectUserSubscriptionTier(state),
      subscriptionResolved: selectIsSubscriptionResolved(state),
    }))
  );
  const hasUserProfile = Boolean(userProfileId);
  const isInsider = useMemo(
    () => hasInsiderAccess(subscriptionData, subscriptionTier),
    [subscriptionData, subscriptionTier]
  );
  const lovedCapsuleIds = useMemo(() => getLovedTimeCapsuleIds(userProfile), [userProfile]);
  const lovedCapsuleIdsKey = lovedCapsuleIds.join("|");
  useRenderTrace("HomePage", {
    routeState: Boolean(state),
    userId: user?.uid ?? null,
    profileId: userProfileId,
    hasUsersList: Boolean(usersList),
  });

  // PWA
  const { showPrompt, triggerPrompt } = usePwaPrompt();

  // Capsule-sent feedback from location state
  const sentCapsuleId = state?.sentCapsuleId ?? null;
  const showSuccessTrigger = Boolean(state?.showSuccess);
  const showDraftSavedTrigger = Boolean(state?.showDraftSaved);
  const hiddenCapsuleIdFromState = String(state?.hiddenCapsuleId || "").trim();
  const discardedDraftIdFromState = String(state?.discardedDraftId || "").trim();
  const notificationMapFocus = useMemo(() => {
    const params = new URLSearchParams(search || "");
    const focusedCapsuleId = String(params.get("focusedCapsuleId") || "").trim();
    const center = getUsableMapLatLng(params.get("lat"), params.get("lng"));
    return { focusedCapsuleId, center };
  }, [search]);
  const focusedCapsuleIdFromRoute =
    String(state?.focusedCapsuleId || "").trim() || notificationMapFocus.focusedCapsuleId;
  const routeMapCenter = notificationMapFocus.center || state?.center || null;
  const historyFindFromState = state?.historyFind || null;
  const buriedCapsulePreview = state?.buriedCapsulePreview || null;
  const restoredPlaceName = String(state?.selectedPlaceName || "").trim();
  const restoredPlaceAddress = String(state?.selectedPlaceAddress || "").trim();
  const restoredPlaceId = String(state?.selectedPlaceId || "").trim();
  const skipRouteQuickFindPreseed = shouldSkipRouteQuickFindPreseed({
    focusedCapsuleId: focusedCapsuleIdFromRoute,
    hiddenCapsuleId: hiddenCapsuleIdFromState,
  });

  // Dynamic success payload (membership redirect can supply this)
  const incomingSuccessDialog = state?.successDialog ?? null;
  const incomingSuccessDialogKey = state?.successDialogKey ?? null;

  // Local control for success dialog
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successCapsuleId, setSuccessCapsuleId] = useState(null);

  const [successDialogPayload, setSuccessDialogPayload] = useState(null);
  const [successDialogKey, setSuccessDialogKey] = useState(null);
  const [draftSavedDialogOpen, setDraftSavedDialogOpen] = useState(false);
  const [hiddenMapCapsuleIds, setHiddenMapCapsuleIds] = useState(() => {
    try {
      const raw = JSON.parse(sessionStorage.getItem(HIDDEN_MAP_CAPSULE_IDS_KEY) || "[]");
      return new Set(Array.isArray(raw) ? raw.filter(Boolean) : []);
    } catch {
      return new Set();
    }
  });

  // Track whether Street View panorama is active so we can hide MapControls overlay
  const [isStreetViewActive, setIsStreetViewActive] = useState(false);
  const streetViewListenerRef = useRef(null);
  const [mapViewport, setMapViewport] = useState(null);

  // When we arrive from flows, open the success dialog once
  useEffect(() => {
    if (showSuccessTrigger) {
      setSuccessDialogOpen(true);
      setSuccessCapsuleId(sentCapsuleId);
      setMarkerRefreshKey((prev) => prev + 1);

      setSuccessDialogPayload(incomingSuccessDialog);
      setSuccessDialogKey(incomingSuccessDialogKey);

      // Clear the flag from history so reloads do not retrigger
      navigate(".", {
        replace: true,
        state: {
          ...state,
          showSuccess: false,
          successDialog: null,
          successDialogKey: null,
        },
      });
    }
  }, [
    showSuccessTrigger,
    sentCapsuleId,
    state,
    navigate,
    incomingSuccessDialog,
    incomingSuccessDialogKey,
  ]);

  useEffect(() => {
    if (!showDraftSavedTrigger) return;
    setDraftSavedDialogOpen(true);
    navigate(".", {
      replace: true,
      state: {
        ...state,
        showDraftSaved: false,
      },
    });
  }, [showDraftSavedTrigger, navigate, state]);

  useEffect(() => {
    if (!hiddenCapsuleIdFromState) return;
    setHiddenMapCapsuleIds((prev) => {
      const next = new Set(prev);
      next.add(hiddenCapsuleIdFromState);
      try {
        sessionStorage.setItem(
          HIDDEN_MAP_CAPSULE_IDS_KEY,
          JSON.stringify(Array.from(next))
        );
      } catch {
        // Ignore storage failures.
      }
      return next;
    });
  }, [hiddenCapsuleIdFromState]);

  useEffect(() => {
    if (!discardedDraftIdFromState) return;

    setHiddenMapCapsuleIds((prev) => {
      const next = new Set(prev);
      next.add(discardedDraftIdFromState);
      return next;
    });
    setMarkerRefreshKey((prev) => prev + 1);
    navigate(".", {
      replace: true,
      state: {
        ...state,
        discardedDraftId: "",
      },
    });
  }, [discardedDraftIdFromState, navigate, state]);

  // Sender profiles for marker name lookup are fetched incrementally, scoped to the
  // senders actually present on the current map (see effect after useHomeMapCapsulesV2).
  const fetchedSenderIdsRef = useRef(new Set());

  // Single map ref
  const mapRef = useRef(null);

  const {
    userLocation,
    directions,
    travelTime,
    destination: navigationDestination,
    isNavigating,
    handleRecenter,
    calculateRoute,
    clearNavigation,
    permissionState,
    requestInitialLocation,
    isLocating,
    geoError,
  } = useUserNavigation(mapRef);

  const {
    permissionDialogOpen,
    ensureLocationOrPrompt,
    closePrimer,
  } = useGeoPermissionPrimer({
    permissionState,
    userLocation,
    requestInitialLocation,
  });
  const {
    pushPrimerOpen,
    queuePushPrimer,
    closePushPrimer,
    requestPushNotifications,
  } = usePushPermissionPrimer({ enabled: Boolean(currentUserId) });
  const pushPrimerTimerRef = useRef(null);

  const handleEnableLocationFromPrimer = useCallback(() => {
    closePrimer();
    requestInitialLocation();
    if (pushPrimerTimerRef.current) {
      window.clearTimeout(pushPrimerTimerRef.current);
    }
    pushPrimerTimerRef.current = window.setTimeout(() => {
      pushPrimerTimerRef.current = null;
      queuePushPrimer();
    }, 650);
  }, [closePrimer, queuePushPrimer, requestInitialLocation]);

  const { isLoaded, loadError } = useLoadScript({
    id: "google-maps-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
    version: "beta",
  });

  const {
    center: mapCenter,
    searchText,
    setSearchText,
    onMapLoad,
    onCenterChange,
    applyPassiveCenter,
    recenter: recenterMap,
    mapCenterSource,
    isResolvingInitialCenter,
  } = useMapCenterAndSearch(mapRef, routeMapCenter, userLocation, {
    permissionState,
    ipFallbackEnabled: true,
    ipFallbackForce: false,
    ipFallbackOnlyWhenNoStoredCenter: true,
    ipFallbackZoom: 18,
  });
  const notificationMapCenterKey = notificationMapFocus.center
    ? `${notificationMapFocus.center.lat},${notificationMapFocus.center.lng}`
    : "";
  useEffect(() => {
    if (!notificationMapFocus.center) return;
    applyPassiveCenter(notificationMapFocus.center, "initial");
  }, [applyPassiveCenter, notificationMapCenterKey, notificationMapFocus.center]);
  const isMobileDevice = useMemo(() => isMobileUserAgent(), []);
  const isIOSSafariBrowser = useMemo(() => isIOSSafari(), []);
  const isAndroidChrome = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = String(navigator.userAgent || "");
    const isAndroid = /Android/i.test(ua);
    const isChromium = /Chrome|CriOS/i.test(ua);
    const isFirefox = /Firefox|FxiOS/i.test(ua);
    const isEdge = /EdgA|EdgiOS/i.test(ua);
    const isSamsungBrowser = /SamsungBrowser/i.test(ua);
    return isAndroid && isChromium && !isFirefox && !isEdge && !isSamsungBrowser;
  }, []);

  const [noCapsulesFoundMessage, setNoCapsulesFoundMessage] = useState("");
  useEffect(() => {
    const ref = doc(db, "config", "app");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setNoCapsulesFoundMessage(data.noCapsulesFoundMessage || "");
      },
      (err) => {
        console.warn("Failed to load noCapsulesFoundMessage:", err);
        setNoCapsulesFoundMessage("");
      }
    );
    return () => unsub();
  }, []);

  // Optional: location-based notifications, only when we have userLocation
  useEffect(() => {
    if (!userLocation || !user) return;
    (async () => {
      const caps = await fetchUnCoverTimeCapsules({
        center: userLocation,
        isUnCover: false,
        radius: PRIVATE_NEARBY_NOTIFICATION_RADIUS_METERS,
      });
      if (caps.length) {
        const fn = httpsCallable(functions, "sendLocationBaseNotification");
        try {
          await fn({ capsules: caps, userId: user.uid });
        } catch (e) {
          console.error(e);
        }
      }
    })();
  }, [userLocation, user]);

  // Fall back to the app default center if no real center resolves within the
  // cap, so a slow GPS prompt / center cascade can't indefinitely block the map
  // render and the nearby-discovery (gems/offers) fetch. Fires once per mount;
  // a real center (GPS/GeoIP) still wins via preferredCenter once it arrives.
  const [centerWaitCapReached, setCenterWaitCapReached] = useState(false);
  useEffect(() => {
    const timerId = setTimeout(
      () => setCenterWaitCapReached(true),
      HOME_CENTER_WAIT_CAP_MS
    );
    return () => clearTimeout(timerId);
  }, []);

  const homeMapCenterState = useMemo(
    () =>
      resolveHomeMapCenterState({
        mapCenter,
        routeMapCenter,
        userLocation,
        defaultCenter: DEFAULT_CENTER,
        permissionState,
        isLocating,
        geoError,
        preferMapCenter: mapCenterSource === "user",
        isResolvingMapCenter: isResolvingInitialCenter,
        waitCapReached: centerWaitCapReached,
      }),
    [
      mapCenter,
      mapCenterSource,
      isResolvingInitialCenter,
      routeMapCenter,
      userLocation,
      permissionState,
      isLocating,
      geoError,
      centerWaitCapReached,
    ]
  );

  // Center used for rendering map and querying clusters/markers even if geo is off.
  const effectiveCenter = homeMapCenterState.effectiveCenter;
  const deferInitialMapRender = homeMapCenterState.deferMapRender;
  // Snap to a coarse grid (~2.2km) so small pans / iOS momentum-scroll jitter
  // keep the same nearby-discovery center key. Without this, the ~1m-precision
  // key churned on every tiny map move, hiding the gems button and restarting
  // the fetch debounce so it rarely settled. The 15km query radius makes the
  // snap immaterial to which gems/offers are relevant.
  const offerQueryCenter = useMemo(() => {
    const liveCenter =
      getUsableMapLatLng(mapViewport?.center?.lat, mapViewport?.center?.lng) || effectiveCenter;
    return snapCenterToGrid(liveCenter) || liveCenter;
  }, [effectiveCenter, mapViewport?.center?.lat, mapViewport?.center?.lng]);
  const [nearbyOffers, setNearbyOffers] = useState([]);
  const [nearbyGems, setNearbyGems] = useState([]);
  const [nearbyOffersCenterKey, setNearbyOffersCenterKey] = useState("");
  const [nearbyGemsCenterKey, setNearbyGemsCenterKey] = useState("");
  const [offersLoading, setOffersLoading] = useState(false);
  const [offersError, setOffersError] = useState("");
  const [offersSheetOpen, setOffersSheetOpen] = useState(false);
  const [activeNearbyShortcut, setActiveNearbyShortcut] = useState("all");
  const [highlightedNearbyGemId, setHighlightedNearbyGemId] = useState("");
  const nearbySheetScrollRef = useRef(null);
  const contactGemRef = useRef(null);
  const lovedGemRef = useRef(null);
  const memberGemRef = useRef(null);
  const offerCenterKey = buildOfferCenterKey(offerQueryCenter);
  const visibleNearbyOffers = useMemo(
    () => (nearbyOffersCenterKey === offerCenterKey ? nearbyOffers : []),
    [nearbyOffers, nearbyOffersCenterKey, offerCenterKey]
  );
  const visibleNearbyGems = useMemo(
    () => (nearbyGemsCenterKey === offerCenterKey ? nearbyGems : []),
    [nearbyGems, nearbyGemsCenterKey, offerCenterKey]
  );
  const nearbyShortcutState = useMemo(
    () => getNearbyShortcutState(visibleNearbyGems),
    [visibleNearbyGems]
  );
  const visibleNearbyGemSections = useMemo(
    () => groupNearbyGemsBySection(visibleNearbyGems),
    [visibleNearbyGems]
  );
  const firstContactGemId = useMemo(
    () => visibleNearbyGems.find((gem) => gem?.gemKind === "received")?.id || "",
    [visibleNearbyGems]
  );
  const firstLovedGemId = useMemo(
    () => visibleNearbyGems.find((gem) => gem?.gemKind === "loved")?.id || "",
    [visibleNearbyGems]
  );
  const firstMemberGemId = useMemo(
    () => visibleNearbyGems.find((gem) => gem?.gemKind === "memberExclusive")?.id || "",
    [visibleNearbyGems]
  );
  const nearbySheetTitle = getNearbyDiscoverySheetTitle({
    offersCount: visibleNearbyOffers.length,
    gemsCount: visibleNearbyGems.length,
  });
  const liveNearbyDiscoveryCount = visibleNearbyOffers.length + visibleNearbyGems.length;
  const [nearbyDiscoveryDisplayCount, setNearbyDiscoveryDisplayCount] = useState(0);

  // Surfacing a row in the nearby-discovery drawer is an impression (§7). Studio
  // offers have no map pin, so the drawer is their only impression surface;
  // capsule-backed rows are typically already deduped by the map impression pass.
  // Fires once per open — the per-campaign/day throttle guards re-opens.
  useEffect(() => {
    if (!offersSheetOpen) return;
    const meta = { surface: "drawer" };
    visibleNearbyOffers.forEach((offer) => {
      if (offer?.capsule) logCapsuleImpression(offer.capsule, { meta });
      else logOfferImpression(offer, { meta });
    });
    visibleNearbyGems.forEach((gem) => logCapsuleImpression(gem?.raw, { meta }));
  }, [offersSheetOpen, visibleNearbyOffers, visibleNearbyGems]);

  // A tap on a drawer row is an `open` (§3.5 step 3). Capsule-backed rows open on
  // the capsule campaign (measurable = featured only); studio offers open on the
  // raffle/coupon campaign. Tagged `surface=drawer` for slicing where dedup allows.
  const logNearbyOfferOpen = useCallback((offer) => {
    const meta = { surface: "drawer" };
    if (offer?.capsule) {
      if (isMeasurableCapsule(offer.capsule)) {
        logCapsuleEvent("open", offer.capsule, { meta });
      }
      return;
    }
    logOfferEvent("open", offer, { meta });
  }, []);

  const logNearbyGemOpen = useCallback((gem) => {
    if (isMeasurableCapsule(gem?.raw)) {
      logCapsuleEvent("open", gem.raw, { meta: { surface: "drawer" } });
    }
  }, []);

  useEffect(() => {
    if (offersLoading) {
      if (liveNearbyDiscoveryCount > 0) {
        setNearbyDiscoveryDisplayCount(liveNearbyDiscoveryCount);
      }
      return;
    }
    setNearbyDiscoveryDisplayCount(liveNearbyDiscoveryCount);
  }, [liveNearbyDiscoveryCount, offersLoading]);

  useEffect(() => {
    if (!shouldLoadNearbyDiscovery({ offerCenterKey, offerQueryCenter })) {
      setOffersLoading(false);
      setNearbyOffers([]);
      setNearbyGems([]);
      setNearbyOffersCenterKey("");
      setNearbyGemsCenterKey("");
      setOffersError("");
      setOffersSheetOpen(false);
      setActiveNearbyShortcut("all");
      setHighlightedNearbyGemId("");
      return undefined;
    }

    setOffersLoading(true);
    setNearbyOffers([]);
    setNearbyGems([]);
    setNearbyOffersCenterKey("");
    setNearbyGemsCenterKey("");
    setOffersError("");
    setOffersSheetOpen(false);
    setActiveNearbyShortcut("all");
    setHighlightedNearbyGemId("");

    const queryCenter = {
      lat: Number(offerQueryCenter.lat),
      lng: Number(offerQueryCenter.lng),
    };
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setOffersError("");
      try {
        const [offerResult, gemResult, lovedResult] = await Promise.allSettled([
          fetchNearbyCampaignOffers({ center: queryCenter }),
          fetchNearbyGems({
            center: queryCenter,
            viewerUid: currentUserId || "",
            radius: NEARBY_GEMS_RADIUS_METERS,
          }),
          currentUserId && lovedCapsuleIds.length
            ? fetchLovedTimeCapsules(lovedCapsuleIds, currentUserId)
            : Promise.resolve([]),
        ]);
        const {
          offers: offerRows,
          gems: gemRows,
          hasAnyFailure,
          hasAnySuccess,
        } = resolveNearbyDiscoveryResults({ offerResult, gemResult });
        if (hasAnyFailure) {
          console.warn("Nearby discovery source failed:", { offerResult, gemResult });
        }
        if (lovedResult?.status === "rejected") {
          console.warn("Nearby loved capsules failed:", lovedResult.reason);
        }
        if (!cancelled) {
          const lovedCapsules =
            lovedResult?.status === "fulfilled" && Array.isArray(lovedResult.value)
              ? lovedResult.value
              : [];
          // Offers carry a businessId but no business name. The lookup is cached
          // for five minutes and is normally the same businesses the gems just
          // fetched, so this usually costs no extra reads.
          const offerBusinessIds = Array.from(
            new Set(
              offerRows.map((offer) => String(offer?.businessId || "").trim()).filter(Boolean)
            )
          );
          const offerBusinessMap = offerBusinessIds.length
            ? await getSponsoredBusinessMarkerMap(offerBusinessIds).catch(() => ({}))
            : {};
          // The lookup above is async, so a superseded run can still land here after
          // a newer one already set `cancelled`. Re-check it — otherwise the stale
          // closure would repopulate the drawer with the previous location's data.
          if (cancelled) return;
          const offersWithBusiness = offerRows.map((offer) => ({
            ...offer,
            companyName:
              offerBusinessMap[String(offer?.businessId || "").trim()]?.companyName || "",
          }));
          setNearbyOffers(offersWithBusiness);
          setNearbyGems(
            mergeLovedNearbyGems({
              gems: gemRows,
              lovedCapsules,
              center: queryCenter,
              radiusMeters: NEARBY_GEMS_RADIUS_METERS,
            })
          );
          setNearbyOffersCenterKey(offerCenterKey);
          setNearbyGemsCenterKey(offerCenterKey);
          setOffersError(hasAnySuccess ? "" : "Nearby deals and gems are taking a minute to load.");
        }
      } catch (err) {
        console.warn("Failed to load nearby discovery:", err);
        if (!cancelled) {
          setNearbyOffers([]);
          setNearbyGems([]);
          setNearbyOffersCenterKey("");
          setNearbyGemsCenterKey("");
          setOffersError("Nearby deals and gems are taking a minute to load.");
        }
      } finally {
        if (!cancelled) setOffersLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [currentUserId, lovedCapsuleIds, lovedCapsuleIdsKey, offerQueryCenter, offerCenterKey]);

  useEffect(() => {
    if (!offersSheetOpen) {
      setActiveNearbyShortcut("all");
      setHighlightedNearbyGemId("");
    }
  }, [offersSheetOpen]);

  const scrollNearbySheetTo = useCallback((ref, shortcut) => {
    setActiveNearbyShortcut(shortcut);
    const gemId = ref.current?.dataset?.gemId || "";
    setHighlightedNearbyGemId(gemId);
    ref.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    if (gemId) {
      window.setTimeout(() => {
        setHighlightedNearbyGemId((current) => (current === gemId ? "" : current));
      }, 1400);
    }
  }, []);

  const navigateFromNearbySheet = useCallback((targetPath, extraState = {}) => {
    const navigationState = buildHomeMapNavigationState({
      targetPath,
      viewport: mapViewport,
      fallbackCenter: effectiveCenter,
      extraState,
    });
    if (!navigationState.path) return;
    setOffersSheetOpen(false);
    if (navigationState.homeState?.center) {
      navigate(".", {
        replace: true,
        state: {
          ...state,
          ...navigationState.homeState,
        },
      });
    }
    navigate(navigationState.path, {
      state: navigationState.targetState,
    });
  }, [effectiveCenter, mapViewport, navigate, state]);

  useEffect(() => {
    const shouldRecoverMapSurface =
      draftSavedDialogOpen ||
      draftSavedDialogSeenRef.current ||
      Boolean(discardedDraftIdFromState);

    if (!shouldRecoverMapSurface) {
      return undefined;
    }

    draftSavedDialogSeenRef.current = true;
    const timeoutIds = [0, 140, 320].map((delayMs) =>
      setTimeout(() => {
        recoverGoogleMapSurface(mapRef.current, effectiveCenter);
      }, delayMs)
    );

    return () => {
      timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, [discardedDraftIdFromState, draftSavedDialogOpen, effectiveCenter]);

  const [markerRefreshKey, setMarkerRefreshKey] = useState(0);
  const [uncoverCategories, setUncoverCategories] = useState(() =>
    loadSavedUncoverCategories(currentUserId)
  );

  const [markerVisibility, setMarkerVisibility] = useState(() =>
    loadSavedMarkerVisibility(currentUserId)
  );
  const [selectedPlaceName, setSelectedPlaceName] = useState("");
  const [selectedPlaceAddress, setSelectedPlaceAddress] = useState("");
  const [selectedPlaceId, setSelectedPlaceId] = useState("");
  const [selectedPlaceCoords, setSelectedPlaceCoords] = useState(null);
  const optimisticBuriedCapsules = useMemo(
    () => (buriedCapsulePreview ? [buriedCapsulePreview] : []),
    [buriedCapsulePreview]
  );
  const [longPressDraft, setLongPressDraft] = useState(null);
  const [buryConfirmOpen, setBuryConfirmOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasUserDraggedMap, setHasUserDraggedMap] = useState(false);
  const dragSettleTimerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastMapInteractionAtRef = useRef(0);
  const [draftCapsules, setDraftCapsules] = useState([]);
  const draftOpenTimerRef = useRef(null);
  const [uncoverPickerOpen, setUncoverPickerOpen] = useState(false);
  const [findPrompt, setFindPrompt] = useState("");
  const [findVoiceStatus, setFindVoiceStatus] = useState("");
  const [findError, setFindError] = useState("");
  const [findAnswerText, setFindAnswerText] = useState("");
  const [findSearchLocationLabel, setFindSearchLocationLabel] = useState("");
  const [findDestinationCandidates, setFindDestinationCandidates] = useState([]);
  const [findOptimisticCapsules, setFindOptimisticCapsules] = useState([]);
  const [isFindListening, setIsFindListening] = useState(false);
  const [legacyFindHasRecoverableError, setLegacyFindHasRecoverableError] = useState(false);
  const optimisticPublicCapsules = useMemo(
    () => (Array.isArray(findOptimisticCapsules) ? findOptimisticCapsules : []),
    [findOptimisticCapsules]
  );
  const [isUncoverSeeding, setIsUncoverSeeding] = useState(false);
  const [activeQuickFindKey, setActiveQuickFindKey] = useState("");
  const [quickFindCategoryStates, setQuickFindCategoryStates] = useState(buildIdleQuickFindCategoryStates);
  const [findAdInstanceKey, setFindAdInstanceKey] = useState("");
  const [showFindAd, setShowFindAd] = useState(false);
  const [showMapRefreshOverlay, setShowMapRefreshOverlay] = useState(false);
  const [uncoverAnchorCenter, setUncoverAnchorCenter] = useState(null);
  const [clusterModalUiState, setClusterModalUiState] = useState(null);
  const mapRefreshOverlayHideTimerRef = useRef(null);
  const findRecognitionRef = useRef(null);
  const findVoiceTranscriptRef = useRef("");
  const findVoiceSubmitTimerRef = useRef(null);
  const clusterRestoreInFlightRef = useRef(false);
  const draftSavedDialogSeenRef = useRef(false);
  const quickFindPreseedCacheRef = useRef(null);
  const quickFindPreseedInFlightRef = useRef(new Map());
  const quickFindPreseedActiveKeyRef = useRef("");
  // Center+placeId the currently displayed Quick Find badges were seeded for, so a
  // category tap anchors to what the user actually sees rather than the ambient
  // effectiveCenter (which can lag/diverge from a searched destination).
  const quickFindPreseedActiveCenterRef = useRef(null);
  const passiveQuickFindPreseedKeyRef = useRef("");
  const lastRecenterChipCenterRef = useRef(null);
  const initialUserLocationPreseedKeyRef = useRef("");
  const routeCenterPreseedKeyRef = useRef("");
  const SpeechRecognition = useMemo(() => getSpeechRecognition(), []);
  const canUseFindSpeech = Boolean(SpeechRecognition);
  const clearPendingFindVoiceSubmit = useCallback(() => {
    if (findVoiceSubmitTimerRef.current) {
      clearTimeout(findVoiceSubmitTimerRef.current);
      findVoiceSubmitTimerRef.current = null;
    }
  }, []);
  useEffect(() => clearPendingFindVoiceSubmit, [clearPendingFindVoiceSubmit]);

  useEffect(() => {
    const restoredPrompt = String(historyFindFromState?.prompt || "").trim();
    if (!restoredPrompt) return;
    setFindPrompt(restoredPrompt);
    setFindAnswerText("Showing a previous Find from your history.");
  }, [historyFindFromState?.prompt]);

  const handleClusterModalUiStateChange = useCallback((nextUiState) => {
    setClusterModalUiState((prevUiState) =>
      areClusterUiStatesEqual(prevUiState, nextUiState) ? prevUiState : nextUiState
    );
  }, []);

  useEffect(() => {
    const saved = loadSavedUncoverCategories(currentUserId);
    setUncoverCategories(saved);
  }, [currentUserId]);

  useEffect(() => {
    setMarkerVisibility(loadSavedMarkerVisibility(currentUserId));
  }, [currentUserId]);

  useEffect(() => {
    saveMarkerVisibilityPreference(currentUserId, markerVisibility);
  }, [currentUserId, markerVisibility]);

  const handleToggleMarkerVisibility = useCallback((key) => {
    if (!HOME_MAP_CATEGORY_FILTERS_ENABLED) return;
    const normalizedKey = key === "sent" ? "buried" : key;
    if (!["received", "buried", "personal", "drafts", "public"].includes(normalizedKey)) {
      return;
    }
    setMarkerVisibility((prev) => ({
      ...prev,
      [normalizedKey]: !(prev[normalizedKey] !== false),
    }));
  }, []);

  useEffect(() => {
    return () => {
      try {
        findRecognitionRef.current?.stop?.();
      } catch {
        // Ignore speech recognition cleanup errors.
      }
    };
  }, []);

  const {
    markers: visibleMarkers,
    displayItems: mapDisplayItems,
    loading: isMapCapsuleLoading,
  } = useHomeMapCapsulesV2({
    center: effectiveCenter,
    viewport: mapViewport,
    refreshKey: markerRefreshKey,
    usersList,
    visibility: HOME_MAP_CATEGORY_FILTERS_ENABLED ? markerVisibility : DEFAULT_MARKER_VISIBILITY,
    hiddenMarkerIds: hiddenMapCapsuleIds,
    optimisticCapsules: optimisticBuriedCapsules,
    optimisticPublicCapsules,
    focusedCapsuleId: focusedCapsuleIdFromRoute,
    userId: currentUserId,
  });

  // impression — a featured capsule surfaced on the map (§3.5, `source=map`).
  // logCapsuleImpression self-gates to measurable capsules and throttles per
  // campaign/day client-side; the server dedups as the backstop.
  useEffect(() => {
    (Array.isArray(visibleMarkers) ? visibleMarkers : []).forEach((marker) => {
      logCapsuleImpression(marker);
    });
  }, [visibleMarkers]);

  // Incrementally load sender profiles for the markers currently on the map, instead of
  // fetching the entire users collection. Missing senders are still resolved live by
  // useCapsuleCluster, so a partial list only ever improves label coverage.
  useEffect(() => {
    const senderIds = Array.from(
      new Set(
        (Array.isArray(visibleMarkers) ? visibleMarkers : [])
          .map((marker) => String(marker?.senderId || "").trim())
          .filter(Boolean)
      )
    );
    const missing = senderIds.filter((id) => !fetchedSenderIdsRef.current.has(id));
    if (!missing.length) return;

    missing.forEach((id) => fetchedSenderIdsRef.current.add(id));
    fetchUsersBasicInfoByIds(missing)
      .then((users) => {
        if (!users.length) return;
        setUsersList((prev) => {
          const byId = new Map(
            (Array.isArray(prev) ? prev : []).map((user) => [user.uid, user])
          );
          users.forEach((user) => {
            if (user?.uid) byId.set(user.uid, user);
          });
          return Array.from(byId.values());
        });
      })
      .catch(console.error);
  }, [visibleMarkers]);

  useEffect(() => {
    if (isMapCapsuleLoading) {
      if (mapRefreshOverlayHideTimerRef.current) {
        clearTimeout(mapRefreshOverlayHideTimerRef.current);
        mapRefreshOverlayHideTimerRef.current = null;
      }
      setShowMapRefreshOverlay(true);
      return;
    }

    mapRefreshOverlayHideTimerRef.current = setTimeout(() => {
      setShowMapRefreshOverlay(false);
      mapRefreshOverlayHideTimerRef.current = null;
    }, 220);

    return () => {
      if (mapRefreshOverlayHideTimerRef.current) {
        clearTimeout(mapRefreshOverlayHideTimerRef.current);
        mapRefreshOverlayHideTimerRef.current = null;
      }
    };
  }, [isMapCapsuleLoading]);

  useEffect(() => {
    if (!restoredPlaceName && !restoredPlaceAddress && !restoredPlaceId) return;
    setSelectedPlaceName(restoredPlaceName);
    setSelectedPlaceAddress(restoredPlaceAddress);
    setSelectedPlaceId(restoredPlaceId);
    if (state?.center && Number.isFinite(state.center.lat) && Number.isFinite(state.center.lng)) {
      setSelectedPlaceCoords({ lat: state.center.lat, lng: state.center.lng });
    }
  }, [restoredPlaceAddress, restoredPlaceId, restoredPlaceName, state?.center]);

  const {
    open: isClusterOpen,
    markers: clusterMarkers,
    mode: clusterMode,
    openClusterFromMap,
    openDiscoverFromMarkers,
    openUncoverCluster,
    close: closeCluster,
  } = useCapsuleCluster(effectiveCenter, usersList);

  useEffect(() => {
    if (!state?.suppressClusterRestore) return;
    clearClusterReturnState();
    setClusterModalUiState(null);
    closeCluster();
    navigate(".", {
      replace: true,
      state: {
        ...state,
        suppressClusterRestore: false,
      },
    });
  }, [closeCluster, navigate, state]);

  const handleSelectDraft = useCallback(
    (draft) => {
      if (!draft) return;

      const destination =
        getRouteDestination(draft) ||
        getUsableMapLatLng(draft?.location?.latitude, draft?.location?.longitude) ||
        getUsableMapLatLng(draft?.location?.latitude, draft?.location?.lon) ||
        getUsableMapLatLng(draft?.latitude, draft?.longitude) ||
        getUsableMapLatLng(draft?.latitude, draft?.lon) ||
        getUsableMapLatLng(effectiveCenter?.lat, effectiveCenter?.lng) ||
        DEFAULT_CENTER;
      const { lat, lng } = destination;
      const address = draft?.address || draft?.location?.address || "";
      const place = draft?.location?.placeName || "";
      const placeId = draft?.location?.placeId || "";

      closeCluster();
      if (draftOpenTimerRef.current) {
        clearTimeout(draftOpenTimerRef.current);
      }
      draftOpenTimerRef.current = setTimeout(() => {
        navigate(
          `/timecapsule?lat=${lat}&lng=${lng}` +
            `&address=${encodeURIComponent(address)}` +
            `&place=${encodeURIComponent(place)}` +
            `&placeId=${encodeURIComponent(placeId)}`,
          {
            state: {
              capsule: {
                ...draft,
                id: draft.id,
                location: {
                  ...(draft.location || {}),
                  address,
                  placeName: place,
                  placeId,
                },
              },
            },
          }
        );
        draftOpenTimerRef.current = null;
      }, 140);
    },
    [navigate, closeCluster, effectiveCenter]
  );

  const openCapsuleDetails = useCallback((capsule, options = {}) => {
    if (!capsule?.id) return;
    const capsulePosition =
      Number.isFinite(Number(capsule?.position?.lat)) &&
      Number.isFinite(Number(capsule?.position?.lng))
        ? {
            lat: Number(capsule.position.lat),
            lng: Number(capsule.position.lng),
          }
        : Number.isFinite(Number(capsule?.location?.lat)) &&
          Number.isFinite(Number(capsule?.location?.lng))
        ? {
            lat: Number(capsule.location.lat),
            lng: Number(capsule.location.lng),
          }
        : null;
    const returnCenter = resolveHomeMapReturnCenter(
      capsulePosition,
      mapViewport,
      effectiveCenter
    );

    if (returnCenter) {
      onCenterChange(returnCenter);
      navigate(".", {
        replace: true,
        state: {
          ...state,
          center: returnCenter,
        },
      });
    }

    if (options?.fromClusterModal) {
      saveClusterReturnState({
        mode: String(clusterMode || "discover"),
        clusterKey: String(capsule?.locationClusterKey || "").trim(),
        center: returnCenter,
        categories: normalizeUncoverCategories(uncoverCategories),
        markers:
          String(clusterMode || "").trim().toLowerCase() === "discover"
            ? clusterMarkers
            : [],
        uiState: options?.clusterUiState || clusterModalUiState || null,
      });
    }

    if (
      shouldRedirectMemberExclusiveMarkerToMembership(
        capsule,
        subscriptionData || subscriptionTier,
        { subscriptionResolved }
      )
    ) {
      const returnTo = `/capsule/${capsule.id}`;
      // returnTo also rides in the query so it survives the sign-in/sign-up
      // detour (which strips router location state) for signed-out users.
      navigate(`/membership?returnTo=${encodeURIComponent(returnTo)}`, {
        state: {
          source: "home-map",
          returnCenter,
          capsuleId: capsule.id,
          returnTo,
        },
      });
      return;
    }

    navigate(`/capsule/${capsule.id}`, {
      state: {
        capsule,
        source: "home-map",
        returnCenter,
      },
    });
  }, [clusterMarkers, clusterMode, clusterModalUiState, effectiveCenter, mapViewport, navigate, onCenterChange, state, subscriptionData, subscriptionResolved, subscriptionTier, uncoverCategories]);

  const handleMarkerClick = useCallback((m, options = {}) => {
    const clusterKey = String(m?.locationClusterKey || "").trim();
    if (clusterKey && !options.fromClusterModal) {
      const sameLocationMarkers = visibleMarkers.filter(
        (marker) => String(marker?.locationClusterKey || "").trim() === clusterKey
      );
      if (sameLocationMarkers.length > 1) {
        openClusterFromMap(sameLocationMarkers);
        return;
      }
    }

    if (m?.isDraft) {
      handleSelectDraft(m?.draftData || m);
      return;
    }

    if (options.fromClusterModal) {
      openCapsuleDetails(m, {
        fromClusterModal: true,
        clusterUiState: options.uiState || null,
      });
      return;
    }

    mapRef.current?.panTo(m.position);
    closeCluster();
    openCapsuleDetails(m, { fromClusterModal: false });
  }, [closeCluster, handleSelectDraft, openCapsuleDetails, openClusterFromMap, visibleMarkers]);

  useEffect(() => {
    if (state?.suppressClusterRestore) return;
    if (isClusterOpen || isUncoverSeeding) return;
    if (clusterRestoreInFlightRef.current) return;
    const restoreState = loadClusterReturnState();
    if (!restoreState) return;

    const mode = String(restoreState?.mode || "").trim().toLowerCase();
    const restoreUiState = restoreState?.uiState || null;

    if (mode === "cluster") {
      const clusterKey = String(restoreState?.clusterKey || "").trim();
      if (!clusterKey) {
        clearClusterReturnState();
        return;
      }
      const sameLocationMarkers = visibleMarkers.filter(
        (marker) => String(marker?.locationClusterKey || "").trim() === clusterKey
      );
      if (sameLocationMarkers.length > 1) {
        setClusterModalUiState(restoreUiState);
        openClusterFromMap(sameLocationMarkers);
        clearClusterReturnState();
      }
      return;
    }

    if (mode === "discover") {
      clusterRestoreInFlightRef.current = true;
      const categories = normalizeUncoverCategories(restoreState?.categories);
      const restoreCenter = restoreState?.center;
      if (
        restoreCenter &&
        Number.isFinite(restoreCenter?.lat) &&
        Number.isFinite(restoreCenter?.lng)
      ) {
        onCenterChange({
          lat: Number(restoreCenter.lat),
          lng: Number(restoreCenter.lng),
        });
        setHasUserDraggedMap(false);
      }
      setUncoverCategories(categories);
      setClusterModalUiState(restoreUiState);
      const cachedMarkers = Array.isArray(restoreState?.markers)
        ? restoreState.markers
        : [];

      // A Quick Find list is complete as saved — the markers ARE the result, not
      // a first paint waiting on a refetch. Running openUncoverCluster over them
      // rebuilds through mapCapsulesDataToMarker, which drops every
      // discoverOnly capsule, so the restored list was replaced by an empty one
      // and the modal closed itself.
      if (isQuickFindReturnState(restoreState)) {
        openDiscoverFromMarkers(cachedMarkers);
        clusterRestoreInFlightRef.current = false;
        clearClusterReturnState();
        return;
      }

      if (cachedMarkers.length) {
        openDiscoverFromMarkers(cachedMarkers);
      }
      openUncoverCluster({
        categories,
        skipSeeding: true,
      })
        .catch((err) => {
          console.warn("Failed to restore uncover modal:", err);
        })
        .finally(() => {
          clusterRestoreInFlightRef.current = false;
          clearClusterReturnState();
        });
      return;
    }

    clearClusterReturnState();
  }, [isClusterOpen, isUncoverSeeding, onCenterChange, openClusterFromMap, openDiscoverFromMarkers, openUncoverCluster, state?.suppressClusterRestore, visibleMarkers]);

  useEffect(() => {
    if (!isClusterOpen || !user?.uid) {
      if (!user?.uid) setDraftCapsules([]);
      return;
    }

    let canceled = false;
    fetchUserDraftTimeCapsules(user.uid)
      .then((drafts) => {
        if (!canceled) setDraftCapsules(drafts);
      })
      .catch((err) => {
        console.error("Failed to load draft capsules:", err);
        if (!canceled) setDraftCapsules([]);
      });

    return () => {
      canceled = true;
    };
  }, [isClusterOpen, user?.uid]);

  const nearbyDraftCapsules = useMemo(() => {
    if (!Array.isArray(draftCapsules) || !draftCapsules.length) return [];
    if (!effectiveCenter || !Number.isFinite(effectiveCenter.lat) || !Number.isFinite(effectiveCenter.lng)) {
      return [];
    }

    return draftCapsules.filter((draft) => {
      const draftPosition = getRouteDestination(draft);
      return distanceMeters(effectiveCenter, draftPosition) <= NEARBY_DRAFTS_RADIUS_METERS;
    });
  }, [draftCapsules, effectiveCenter]);

  useEffect(() => {
    return () => {
      if (draftOpenTimerRef.current) {
        clearTimeout(draftOpenTimerRef.current);
        draftOpenTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleReset = () => {
      setMarkerVisibility({ ...DEFAULT_MARKER_VISIBILITY });
    };
    window.addEventListener("inssa:home-reset-map-filters", handleReset);
    return () => {
      window.removeEventListener("inssa:home-reset-map-filters", handleReset);
    };
  }, []);

  // Only gate actions that truly require device location: navigation
  const pendingLocationActionRef = useRef(null);
  const lastWalkRouteRequestKeyRef = useRef("");
  const runWhenUserLocationReady = useCallback(
    (fn) => {
      if (userLocation) {
        fn();
        return;
      }

      // Queue first so actions (like "Walk Here") can resume once location arrives.
      pendingLocationActionRef.current = fn;
      const ok = ensureLocationOrPrompt();
      if (!ok) return;
    },
    [ensureLocationOrPrompt, userLocation]
  );

  useEffect(() => {
    if (pendingLocationActionRef.current && userLocation) {
      const toRun = pendingLocationActionRef.current;
      pendingLocationActionRef.current = null;
      try {
        toRun();
      } catch (e) {
        console.error("Queued location action failed:", e);
      }
    }
  }, [userLocation]);

  // Local overlay for denied navigation only
  const [deniedOverlayOpen, setDeniedOverlayOpen] = useState(false);
  const [walkRouteError, setWalkRouteError] = useState({
    open: false,
    status: "",
    destination: null,
  });
  const walkRouteRequest = state?.walkRouteRequest || null;

  const focusFailedWalkDestination = useCallback((destination) => {
    const lat = Number(destination?.lat);
    const lng = Number(destination?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const target = { lat, lng };
    mapRef.current?.panTo(target);
    mapRef.current?.setZoom?.(18);
    onCenterChange(target);
    setHasUserDraggedMap(false);
  }, [onCenterChange]);

  const startWalkRoute = useCallback(
    async (destination) => {
      const lat = Number(destination?.lat);
      const lng = Number(destination?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        setWalkRouteError({
          open: true,
          status: "MISSING_DATA",
          destination: null,
        });
        return;
      }

      try {
        // Prefer an already-known device location; avoid forcing a fresh recenter
        // on every attempt (Android is more sensitive to geolocation timeouts).
        let origin = userLocation || null;
        if (!origin) {
          origin = await new Promise((resolve) => {
            handleRecenter((pos) => resolve(pos || null));
          });
        }

        if (!origin) {
          clearNavigation();
          focusFailedWalkDestination({ lat, lng });
          setWalkRouteError({
            open: true,
            status: "MISSING_DATA",
            destination: { lat, lng },
          });
          return;
        }

        const safeRouteResult = await Promise.race([
          calculateRoute(
            { lat, lng },
            { origin }
          ),
          new Promise((resolve) => {
            setTimeout(
              () => resolve({ ok: false, status: "ROUTE_TIMEOUT" }),
              WALK_ROUTE_TIMEOUT_MS
            );
          }),
        ]);

        if (!safeRouteResult?.ok) {
          clearNavigation();
          focusFailedWalkDestination({ lat, lng });
          setWalkRouteError({
            open: true,
            status: safeRouteResult?.status || "UNKNOWN_ERROR",
            destination: { lat, lng },
          });
          return;
        }

        setWalkRouteError((prev) =>
          prev.open
            ? { open: false, status: "", destination: null }
            : prev
        );
      } catch (err) {
        console.warn("Failed to start walk route from details:", err);
        clearNavigation();
        focusFailedWalkDestination({ lat, lng });
        setWalkRouteError({
          open: true,
          status: "UNKNOWN_ERROR",
          destination: { lat, lng },
        });
      }
    },
    [
      calculateRoute,
      clearNavigation,
      focusFailedWalkDestination,
      handleRecenter,
      userLocation,
    ]
  );

  useEffect(() => {
    if (!isLoaded || !window.google?.maps) return;

    const destination = walkRouteRequest?.destination;
    const lat = Number(destination?.lat);
    const lng = Number(destination?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const requestKey = String(
      walkRouteRequest?.requestedAt || `${lat.toFixed(6)},${lng.toFixed(6)}`
    );
    if (lastWalkRouteRequestKeyRef.current === requestKey) return;
    lastWalkRouteRequestKeyRef.current = requestKey;

    runWhenUserLocationReady(() => {
      startWalkRoute({ lat, lng });
    });

    navigate(".", {
      replace: true,
      state: {
        ...state,
        walkRouteRequest: null,
      },
    });
  }, [
    isLoaded,
    walkRouteRequest,
    runWhenUserLocationReady,
    startWalkRoute,
    navigate,
    state,
  ]);

  const routeToWizard = useCallback(
    ({ lat, lng, address, place, placeId }) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const destination =
        `/timecapsule?lat=${lat}&lng=${lng}` +
        `&address=${encodeURIComponent(address || "")}` +
        `&place=${encodeURIComponent(place || "")}` +
        `&placeId=${encodeURIComponent(placeId || "")}`;

      if (hasUserProfile) {
        navigate(destination, {
          state: { forceFreshSession: true },
        });
        return;
      }

      try {
        sessionStorage.setItem(SIGNIN_REDIRECT_KEY, destination);
      } catch {}
      const nextParam = encodeURIComponent(destination);
      navigate(`/signin?next=${nextParam}`);
    },
    [navigate, hasUserProfile]
  );

  const handleConfirmLocation = useCallback(async () => {
    // Read the live map center so a just-selected autocomplete place
    // doesn't lose its placeId while React state is still catching up.
    const currentCenter = mapRef.current?.getCenter?.();
    const liveCenter =
      currentCenter &&
      typeof currentCenter.lat === "function" &&
      typeof currentCenter.lng === "function"
        ? { lat: currentCenter.lat(), lng: currentCenter.lng() }
        : null;
    const centerToUse = liveCenter || mapCenter;
    if (!centerToUse) return;
    const { lat, lng } = centerToUse;

    const selectedPlaceMatchesCenter =
      selectedPlaceCoords &&
      centerToUse &&
      distanceMeters(selectedPlaceCoords, centerToUse) <= 120;
    const effectivePlaceName = selectedPlaceMatchesCenter ? selectedPlaceName : "";
    const effectivePlaceId = selectedPlaceMatchesCenter ? selectedPlaceId : "";
    const effectivePlaceAddress = selectedPlaceMatchesCenter ? selectedPlaceAddress : "";

    let address = effectivePlaceAddress || searchText || "";
    if (!address && window.google?.maps) {
      const geocoder = new window.google.maps.Geocoder();
      try {
        address = await new Promise((resolve) => {
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results?.[0]?.formatted_address) {
              resolve(results[0].formatted_address);
              return;
            }
            resolve("");
          });
        });
      } catch {
        address = "";
      }
    }

    const fallbackAddress = address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    routeToWizard({
      lat,
      lng,
      address: fallbackAddress,
      place: effectivePlaceName || "",
      placeId: effectivePlaceId || "",
    });
  }, [mapCenter, routeToWizard, searchText, selectedPlaceAddress, selectedPlaceCoords, selectedPlaceId, selectedPlaceName]);

  const handleMapLongPress = useCallback(
    ({ lat, lng, placeId }) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      setLongPressDraft({ lat, lng, address: "", place: "", placeId: placeId || "" });
      setBuryConfirmOpen(true);

      const placeService =
        window.google?.maps?.places && mapRef.current
          ? new window.google.maps.places.PlacesService(mapRef.current)
          : null;
      if (placeService && placeId) {
        placeService.getDetails(
          { placeId, fields: ["name", "formatted_address", "place_id"] },
          (place, status) => {
            if (status === "OK" && place) {
              setLongPressDraft((prev) =>
                prev
                  ? {
                      ...prev,
                      place: place.name || "",
                      address: place.formatted_address || prev.address || "",
                      placeId: place.place_id || placeId || prev.placeId || "",
                    }
                  : prev
              );
            }
          }
        );
      }

      const geocoder = window.google?.maps ? new window.google.maps.Geocoder() : null;
      if (!geocoder) return;
      if (!placeId) {
        // First try exact POI reverse-geocode around the pressed coordinate.
        geocoder.geocode(
          {
            location: { lat, lng },
            resultType: ["point_of_interest", "establishment"],
          },
          (poiResults, poiStatus) => {
            const topPoi = poiStatus === "OK" && poiResults?.[0] ? poiResults[0] : null;
            if (topPoi) {
              if (placeService && topPoi.place_id) {
                placeService.getDetails(
                  { placeId: topPoi.place_id, fields: ["name", "formatted_address"] },
                  (place, status) => {
                    if (status === "OK" && place?.name) {
                      setLongPressDraft((prev) =>
                        prev
                          ? {
                              ...prev,
                              place: place.name || prev.place || "",
                              address: place.formatted_address || prev.address || "",
                              placeId: topPoi.place_id || prev.placeId || "",
                            }
                          : prev
                      );
                    }
                  }
                );
              } else {
                const inferredName = topPoi?.address_components?.[0]?.long_name || "";
                setLongPressDraft((prev) =>
                  prev
                    ? {
                        ...prev,
                        place: inferredName || prev.place || "",
                        address: topPoi.formatted_address || prev.address || "",
                        placeId: topPoi.place_id || prev.placeId || "",
                      }
                    : prev
                );
              }
              return;
            }

            // Fallback only if no POI hit: nearest nearby place, but avoid convenience-store bias.
            if (placeService && window.google?.maps?.places?.RankBy) {
              placeService.nearbySearch(
                {
                  location: { lat, lng },
                  rankBy: window.google.maps.places.RankBy.DISTANCE,
                },
                (results, status) => {
                  if (status !== "OK" || !Array.isArray(results) || results.length === 0) return;
                  const top = results.filter((r) => r?.name && r?.geometry?.location);
                  if (!top.length) return;

                  const nonConvenience = top.find((r) => {
                    const t = Array.isArray(r?.types) ? r.types : [];
                    return !t.some((v) => ["convenience_store", "gas_station", "supermarket"].includes(v));
                  });
                  const chosen = nonConvenience || top[0];

                  setLongPressDraft((prev) =>
                    prev
                      ? {
                          ...prev,
                          place: chosen.name || prev.place || "",
                          address: chosen.vicinity || chosen.formatted_address || prev.address || "",
                          placeId: chosen.place_id || prev.placeId || "",
                        }
                      : prev
                  );
                }
              );
            }
          }
        );
      }

      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          setLongPressDraft((prev) =>
            prev
              ? {
                  ...prev,
                  place: prev.place || "",
                  address: results[0].formatted_address || "",
                }
              : prev
          );
        }
      });
    },
    []
  );

  const handlePoiClick = useCallback(
    ({ placeId, lat, lng }) => {
      if (!placeId || !mapRef.current || !window.google?.maps?.places) return;
      const service = new window.google.maps.places.PlacesService(mapRef.current);
      service.getDetails(
        { placeId, fields: ["name", "formatted_address", "geometry", "place_id"] },
        (place, status) => {
          if (status !== "OK" || !place) return;
          const geoLat = place?.geometry?.location?.lat?.();
          const geoLng = place?.geometry?.location?.lng?.();
          const newPos = {
            lat: Number.isFinite(geoLat) ? geoLat : lat,
            lng: Number.isFinite(geoLng) ? geoLng : lng,
          };
          if (!Number.isFinite(newPos.lat) || !Number.isFinite(newPos.lng)) return;

          const map = mapRef.current;
          map?.panTo(newPos);
          const targetZoom = 17;
          const startZoom = Number(map?.getZoom?.()) || targetZoom;
          if (Number.isFinite(startZoom) && startZoom < targetZoom) {
            let z = startZoom;
            const zoomTimer = setInterval(() => {
              z += 1;
              map?.setZoom?.(z);
              if (z >= targetZoom) {
                clearInterval(zoomTimer);
              }
            }, 70);
          } else if (Number.isFinite(startZoom) && startZoom > targetZoom) {
            map?.setZoom?.(targetZoom);
          }
          setSearchText(place.name || place.formatted_address || "");
          setSelectedPlaceName(place.name || "");
          setSelectedPlaceAddress(place.formatted_address || place.name || "");
          setSelectedPlaceId(place.place_id || placeId || "");
          setSelectedPlaceCoords(newPos);
          // Commit center after pan starts to avoid a hard recenter snap.
          setTimeout(() => onCenterChange(newPos), 220);
        }
      );
    },
    [onCenterChange, setSearchText]
  );

  // Guard access to google.maps enums until the script has loaded.
  const streetViewPosition = useMemo(() => {
    if (!isLoaded) return null;
    const g = window.google;
    if (!g || !g.maps || !g.maps.ControlPosition) return null;
    return g.maps.ControlPosition.RIGHT_CENTER;
  }, [isLoaded]);

  const isMobileMapUi = useMemo(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return (
      window.matchMedia("(pointer: coarse)").matches ||
      window.matchMedia("(max-width: 900px)").matches
    );
  }, []);

  const mapOptions = useMemo(() => {
    const isDarkMode = theme.palette.mode === "dark";
    const base = {
      disableDefaultUI: true,
      zoomControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      styles: isDarkMode ? GOOGLE_MAP_DARK_STYLES : undefined,
      backgroundColor: theme.colors.mapBackground,
      draggable: !isUncoverSeeding,
      scrollwheel: !isUncoverSeeding,
      disableDoubleClickZoom: isUncoverSeeding,
      keyboardShortcuts: !isMobileMapUi && !isUncoverSeeding,
      gestureHandling: isUncoverSeeding ? "none" : "greedy",
    };

    if (streetViewPosition) {
      return {
        ...base,
        streetViewControlOptions: { position: streetViewPosition },
      };
    }

    return base;
  }, [
    streetViewPosition,
    isMobileMapUi,
    isUncoverSeeding,
    theme.palette.mode,
    theme.colors.mapBackground,
  ]);

  const containerStyle = useMemo(
    () => ({
      width: "100%",
      height: `calc(var(--vh, 1vh) * 100 - ${NAVBAR_HEIGHT}px)`,
    }),
    []
  );
  const mapContainerStyle = useMemo(
    () => ({
      width: "100%",
      height: "100%",
    }),
    []
  );

  const shouldShowInstallPrompt = showPrompt && !isPwaInstalled();

  const updateMapViewportSnapshot = useCallback((map = null) => {
    const activeMap = map || mapRef.current;
    if (!activeMap) return;

    const center = activeMap.getCenter?.();
    const bounds = activeMap.getBounds?.();
    const ne = bounds?.getNorthEast?.();
    const sw = bounds?.getSouthWest?.();
    const zoom = Number(activeMap.getZoom?.());
    const nextViewport = {
      zoom: Number.isFinite(zoom) ? zoom : 18,
      center:
        center && typeof center.lat === "function" && typeof center.lng === "function"
          ? { lat: center.lat(), lng: center.lng() }
          : null,
      bounds:
        ne && sw && typeof ne.lat === "function" && typeof ne.lng === "function"
          ? {
              north: ne.lat(),
              east: ne.lng(),
              south: sw.lat(),
              west: sw.lng(),
            }
          : null,
    };

    setMapViewport((prev) => {
      const prevZoom = Number(prev?.zoom);
      const prevCenter = prev?.center || {};
      const prevBounds = prev?.bounds || {};
      const nextCenter = nextViewport.center || {};
      const nextBounds = nextViewport.bounds || {};
      const unchanged =
        prev &&
        prevZoom === nextViewport.zoom &&
        prevCenter.lat === nextCenter.lat &&
        prevCenter.lng === nextCenter.lng &&
        prevBounds.north === nextBounds.north &&
        prevBounds.east === nextBounds.east &&
        prevBounds.south === nextBounds.south &&
        prevBounds.west === nextBounds.west;
      return unchanged ? prev : nextViewport;
    });
  }, []);

  // Wrap onMapLoad so we can detect Street View entering/exiting and hide MapControls overlay.
  const handleMapLoad = useCallback(
    (map) => {
      onMapLoad(map);
      updateMapViewportSnapshot(map);

      try {
        const panorama =
          map && typeof map.getStreetView === "function"
            ? map.getStreetView()
            : null;

        if (!panorama) return;

        const update = () => {
          const visible =
            typeof panorama.getVisible === "function"
              ? panorama.getVisible()
              : false;
          setIsStreetViewActive(Boolean(visible));
        };

        update();

        if (
          streetViewListenerRef.current &&
          typeof streetViewListenerRef.current.remove === "function"
        ) {
          streetViewListenerRef.current.remove();
        }

        streetViewListenerRef.current = panorama.addListener(
          "visible_changed",
          update
        );
      } catch (e) {
        console.warn("[HomePage] Street View listener setup failed:", e);
      }
    },
    [onMapLoad, updateMapViewportSnapshot]
  );

  const restoreQuickFindCategoriesFromCache = useCallback(({ center, placeId = "" } = {}) => {
    const activeCenter = getUsableMapLatLng(center?.lat, center?.lng);
    if (!activeCenter) return null;
    if (!quickFindPreseedCacheRef.current) {
      quickFindPreseedCacheRef.current = loadQuickFindPreseedCache();
    }
    const cache = quickFindPreseedCacheRef.current;
    const normalizedPlaceId = String(placeId || "").trim();
    const exactCacheKey = buildQuickFindPreseedCacheKey(activeCenter, QUICK_FIND_PRESEED_KEY, normalizedPlaceId);
    const nearbySeed = !normalizedPlaceId
      ? findFreshQuickFindPreseedNearCenter(cache, {
          center: activeCenter,
          categoryKey: QUICK_FIND_PRESEED_KEY,
          ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
          radiusMeters: QUICK_FIND_PRESEED_REUSE_RADIUS_METERS,
        })
      : null;
    const exactCacheFresh = exactCacheKey &&
      hasFreshQuickFindCategoryCacheSet({
        cache,
        combinedCacheKey: exactCacheKey,
        categoryKeys: QUICK_FIND_CATEGORY_KEYS,
        ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
      });
    const nearbyCacheFresh = nearbySeed?.cacheKey &&
      hasFreshQuickFindCategoryCacheSet({
        cache,
        combinedCacheKey: nearbySeed.cacheKey,
        categoryKeys: QUICK_FIND_CATEGORY_KEYS,
        ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
      });
    const combinedCacheKey = exactCacheFresh ? exactCacheKey : nearbyCacheFresh ? nearbySeed.cacheKey : "";
    if (!combinedCacheKey) return null;
    quickFindPreseedActiveCenterRef.current = { center: activeCenter, placeId: normalizedPlaceId };

    const capsules = QUICK_FIND_CATEGORY_KEYS.flatMap((categoryKey) => {
      const categoryCacheKey = combinedCacheKey.replace(/:[^:]+$/, `:${categoryKey}`);
      return resolveQuickFindCachedCapsules({
        cache,
        categoryCacheKey,
        ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
      });
    });
    if (capsules.length) {
      setFindOptimisticCapsules((prev) => mergeCapsulesById(prev, capsules));
    }
    setQuickFindCategoryStates(
      buildQuickFindCategoryStatesFromCache({
        cache,
        combinedCacheKey,
        categoryKeys: QUICK_FIND_CATEGORY_KEYS,
        ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
        minReadyCount: QUICK_FIND_MIN_READY_COUNT,
      })
    );
    setMarkerRefreshKey((prev) => prev + 1);
    return { cacheKey: combinedCacheKey, capsules };
  }, []);

  const preseedQuickFindCategories = useCallback(async ({ center, placeId = "", trigger = "background" } = {}) => {
    const activeCenter = getUsableMapLatLng(center?.lat, center?.lng);
    if (!activeCenter) return null;

    if (!quickFindPreseedCacheRef.current) {
      quickFindPreseedCacheRef.current = loadQuickFindPreseedCache();
    }
    const cache = quickFindPreseedCacheRef.current;
    const locationKey = buildQuickFindPreseedLocationKey(activeCenter, placeId);
    const cacheKey = buildQuickFindPreseedCacheKey(activeCenter, QUICK_FIND_PRESEED_KEY, placeId);
    if (!locationKey || !cacheKey) return null;

    quickFindPreseedActiveKeyRef.current = cacheKey;
    const normalizedTrigger = String(trigger || "background").trim().toLowerCase();
    const normalizedPlaceId = String(placeId || "").trim();
    quickFindPreseedActiveCenterRef.current = { center: activeCenter, placeId: normalizedPlaceId };
    const shouldReuseNearbyQuietSeed = normalizedTrigger !== "search" && !normalizedPlaceId;
    const canUpdateCurrentLocation = () => quickFindPreseedActiveKeyRef.current === cacheKey;
    const setCategoryStatesFromCache = (combinedCacheKey, { loading = false } = {}) => {
      if (!canUpdateCurrentLocation()) return;
      setQuickFindCategoryStates(
        buildQuickFindCategoryStatesFromCache({
          cache,
          combinedCacheKey,
          categoryKeys: QUICK_FIND_CATEGORY_KEYS,
          loading,
          ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
          minReadyCount: QUICK_FIND_MIN_READY_COUNT,
        })
      );
    };

    const restoreFromCache = (combinedCacheKey) => {
      const capsules = QUICK_FIND_CATEGORY_KEYS.flatMap((categoryKey) => {
        const categoryCacheKey = buildQuickFindPreseedCacheKey(activeCenter, categoryKey, normalizedPlaceId);
        const effectiveCategoryCacheKey = combinedCacheKey === cacheKey
          ? categoryCacheKey
          : String(combinedCacheKey || "").replace(/:[^:]+$/, `:${categoryKey}`);
        return resolveQuickFindCachedCapsules({
          cache,
          categoryCacheKey: effectiveCategoryCacheKey,
          ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
        });
      });
      if (capsules.length) {
        setFindOptimisticCapsules((prev) => mergeCapsulesById(prev, capsules));
      }
      setCategoryStatesFromCache(combinedCacheKey);
      setMarkerRefreshKey((prev) => prev + 1);
      return { cacheKey: combinedCacheKey, capsules };
    };

    if (
      hasFreshQuickFindCategoryCacheSet({
        cache,
        combinedCacheKey: cacheKey,
        categoryKeys: QUICK_FIND_CATEGORY_KEYS,
        ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
      })
    ) {
      return restoreFromCache(cacheKey);
    }

    const nearbyCachedSeed = shouldReuseNearbyQuietSeed
      ? findFreshQuickFindPreseedNearCenter(cache, {
          center: activeCenter,
          categoryKey: QUICK_FIND_PRESEED_KEY,
          ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
          radiusMeters: QUICK_FIND_PRESEED_REUSE_RADIUS_METERS,
        })
      : null;
    if (
      nearbyCachedSeed &&
      hasFreshQuickFindCategoryCacheSet({
        cache,
        combinedCacheKey: nearbyCachedSeed.cacheKey,
        categoryKeys: QUICK_FIND_CATEGORY_KEYS,
        ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
      })
    ) {
      return restoreFromCache(nearbyCachedSeed.cacheKey || cacheKey);
    }

    const nearbyInFlightSeed = shouldReuseNearbyQuietSeed
      ? findQuickFindPreseedInFlightNearCenter(quickFindPreseedInFlightRef.current, {
          center: activeCenter,
          categoryKey: QUICK_FIND_PRESEED_KEY,
          radiusMeters: QUICK_FIND_PRESEED_REUSE_RADIUS_METERS,
        })
      : null;
    if (nearbyInFlightSeed) {
      setCategoryStatesFromCache(nearbyInFlightSeed.cacheKey || cacheKey, { loading: true });
      const result = nearbyInFlightSeed.promise ? await nearbyInFlightSeed.promise : null;
      if (canUpdateCurrentLocation()) {
        restoreFromCache(result?.cacheKey || nearbyInFlightSeed.cacheKey || cacheKey);
      }
      return result;
    }

    const existingSeed = quickFindPreseedInFlightRef.current.get(cacheKey);
    if (existingSeed) {
      setCategoryStatesFromCache(cacheKey, { loading: true });
      const result = existingSeed.promise ? await existingSeed.promise : null;
      if (canUpdateCurrentLocation()) {
        restoreFromCache(result?.cacheKey || cacheKey);
      }
      return result;
    }

    const seedPromise = (async () => {
      setCategoryStatesFromCache(cacheKey, { loading: true });
      const result = await seedQuickFindCategories({
        center: activeCenter,
        placeId: normalizedPlaceId,
        categories: QUICK_FIND_CATEGORY_KEYS,
      });
      const baseCacheEntry = {
        at: Date.now(),
        center: activeCenter,
        locationKey: result?.locationKey || locationKey,
        radiusMeters: result?.radiusMeters || QUICK_FIND_PRESEED_REUSE_RADIUS_METERS,
        minResultsPerCategory: result?.minResultsPerCategory || QUICK_FIND_MIN_READY_COUNT,
        source: "discover_seedQuickFindCategories",
      };
      const fetchedCapsules = [];
      QUICK_FIND_CATEGORY_KEYS.forEach((categoryKey) => {
        const resultCapsules = Array.isArray(result?.categories?.[categoryKey]?.capsules)
          ? result.categories[categoryKey].capsules
          : [];
        const categoryCache = buildQuickFindDirectCategoryCacheEntry({
          baseCacheKey: cacheKey,
          baseEntry: baseCacheEntry,
          categoryKey,
          capsules: resultCapsules,
        });
        if (categoryCache?.cacheKey && categoryCache?.entry) {
          cache[categoryCache.cacheKey] = categoryCache.entry;
        }
        fetchedCapsules.push(...resultCapsules);
      });

      const combinedCapsules = QUICK_FIND_CATEGORY_KEYS.flatMap((categoryKey) => {
        const categoryCacheKey = buildQuickFindPreseedCacheKey(activeCenter, categoryKey, normalizedPlaceId);
        return resolveQuickFindCachedCapsules({
          cache,
          categoryCacheKey,
          ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
        });
      });
      const combinedCapsuleIds = Array.from(
        new Set(
          combinedCapsules
            .map((capsule) => String(capsule?.id || "").trim())
            .filter(Boolean)
        )
      );
      cache[cacheKey] = {
        ...baseCacheEntry,
        capsules: combinedCapsules,
        capsuleIds: combinedCapsuleIds,
        count: combinedCapsuleIds.length,
        categoryKeys: QUICK_FIND_CATEGORY_KEYS,
        backendSeed: true,
      };
      quickFindPreseedCacheRef.current = cache;
      saveQuickFindPreseedCache(cache);

      if (!canUpdateCurrentLocation()) return { result, cacheKey, capsules: fetchedCapsules };

      if (fetchedCapsules.length) {
        setFindOptimisticCapsules((prev) => mergeCapsulesById(prev, fetchedCapsules));
      }
      setCategoryStatesFromCache(cacheKey);
      setMarkerRefreshKey((prev) => prev + 1);
      return { result, cacheKey, capsules: fetchedCapsules };
    })();

    quickFindPreseedInFlightRef.current.set(cacheKey, {
      cacheKey,
      categoryKey: QUICK_FIND_PRESEED_KEY,
      center: activeCenter,
      promise: seedPromise,
    });

    try {
      return await seedPromise;
    } catch (err) {
      console.warn("Quiet quick-find seed failed:", err);
      if (canUpdateCurrentLocation()) {
        setQuickFindCategoryStates(buildIdleQuickFindCategoryStates());
      }
      return null;
    } finally {
      quickFindPreseedInFlightRef.current.delete(cacheKey);
    }
  }, []);

  useEffect(() => {
    const passiveCenter = resolveHomeMapPassiveQuickFindCenter({
      effectiveCenter,
      routeMapCenter,
      userLocation,
    });
    const activeCenter = getUsableMapLatLng(passiveCenter?.lat, passiveCenter?.lng);
    if (!activeCenter) return;
    const locationKey = buildQuickFindPreseedLocationKey(activeCenter);
    if (!locationKey || passiveQuickFindPreseedKeyRef.current === locationKey) return;
    passiveQuickFindPreseedKeyRef.current = locationKey;
    preseedQuickFindCategories({ center: activeCenter, trigger: "initial" });
  }, [
    effectiveCenter,
    preseedQuickFindCategories,
    routeMapCenter,
    userLocation,
  ]);

  useEffect(() => {
    const activeUserLocation = getUsableMapLatLng(userLocation?.lat, userLocation?.lng);
    const activeRouteCenter = getUsableMapLatLng(routeMapCenter?.lat, routeMapCenter?.lng);
    if (activeRouteCenter) return;
    if (!activeUserLocation) return;
    const locationKey = buildQuickFindPreseedLocationKey(activeUserLocation);
    if (!locationKey || initialUserLocationPreseedKeyRef.current === locationKey) return;
    initialUserLocationPreseedKeyRef.current = locationKey;
    preseedQuickFindCategories({ center: activeUserLocation, trigger: "initial" });
  }, [preseedQuickFindCategories, routeMapCenter?.lat, routeMapCenter?.lng, userLocation]);

  useEffect(() => {
    const activeRouteCenter = getUsableMapLatLng(routeMapCenter?.lat, routeMapCenter?.lng);
    if (!activeRouteCenter) return;
    if (skipRouteQuickFindPreseed) {
      restoreQuickFindCategoriesFromCache({ center: activeRouteCenter, placeId: restoredPlaceId });
      return;
    }
    const locationKey = buildQuickFindPreseedLocationKey(activeRouteCenter, restoredPlaceId);
    if (!locationKey || routeCenterPreseedKeyRef.current === locationKey) return;
    routeCenterPreseedKeyRef.current = locationKey;
    setQuickFindCategoryStates(buildIdleQuickFindCategoryStates());
    preseedQuickFindCategories({ center: activeRouteCenter, placeId: restoredPlaceId, trigger: "route" });
  }, [
    preseedQuickFindCategories,
    restoreQuickFindCategoriesFromCache,
    restoredPlaceId,
    routeMapCenter?.lat,
    routeMapCenter?.lng,
    skipRouteQuickFindPreseed,
  ]);

  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
    // If user edits text directly, we no longer trust the selected place name.
    setSelectedPlaceName("");
    setSelectedPlaceAddress("");
    setSelectedPlaceId("");
    setSelectedPlaceCoords(null);
  }, [setSearchText]);

  const handlePlaceSelect = useCallback((place) => {
    const loc = place.geometry.location;
    const newPos = { lat: loc.lat(), lng: loc.lng() };
    mapRef.current?.panTo(newPos);
    mapRef.current?.setZoom(18);
    setSearchText(place.name || place.formatted_address || "");
    setSelectedPlaceName(place.name || "");
    setSelectedPlaceAddress(place.formatted_address || place.name || "");
    setSelectedPlaceId(place.place_id || "");
    setSelectedPlaceCoords(newPos);
    onCenterChange(newPos);
    preseedQuickFindCategories({ center: newPos, placeId: place.place_id || "", trigger: "search" });
  }, [onCenterChange, preseedQuickFindCategories, setSearchText]);

  const handleRecenterRequest = useCallback(async () => {
    if (
      permissionState === "denied" ||
      permissionState === "unsupported"
    ) {
      const moved = await recenterMap({ forceIp: true });
      if (!moved) {
        console.warn(
          "[HomePage] GeoIP recenter failed, leaving map as-is."
        );
      } else {
        const center = mapRef.current?.getCenter?.();
        const recenteredPosition =
          center && typeof center.lat === "function" && typeof center.lng === "function"
            ? { lat: center.lat(), lng: center.lng() }
            : null;
        const previousRecenterChipCenter = lastRecenterChipCenterRef.current;
        const shouldSurfaceRecenterChip =
          !previousRecenterChipCenter ||
          !recenteredPosition ||
          distanceMeters(previousRecenterChipCenter, recenteredPosition) >= QUICK_FIND_RECENTER_CHIP_MIN_DISTANCE_METERS;
        if (shouldSurfaceRecenterChip && recenteredPosition) {
          lastRecenterChipCenterRef.current = recenteredPosition;
        }
        preseedQuickFindCategories({
          center: recenteredPosition,
          trigger: "recenter",
        });
      }
      return;
    }

    runWhenUserLocationReady(() => {
      handleRecenter((pos) => {
        applyPassiveCenter(pos, "gps");
        setSelectedPlaceName("");
        setSelectedPlaceAddress("");
        setSelectedPlaceId("");
        setSelectedPlaceCoords(null);
        const previousRecenterChipCenter = lastRecenterChipCenterRef.current;
        const shouldSurfaceRecenterChip =
          !previousRecenterChipCenter ||
          distanceMeters(previousRecenterChipCenter, pos) >= QUICK_FIND_RECENTER_CHIP_MIN_DISTANCE_METERS;
        if (shouldSurfaceRecenterChip) {
          lastRecenterChipCenterRef.current = pos;
        }
        preseedQuickFindCategories({
          center: pos,
          trigger: "recenter",
        });
      });
    });
  }, [
    permissionState,
    recenterMap,
    applyPassiveCenter,
    runWhenUserLocationReady,
    handleRecenter,
    preseedQuickFindCategories,
  ]);

  const handleMapDrag = useCallback(() => {
    if (DEBUG_HOME_MAP_SHOVEL) {
      console.log("[home-map-shovel] drag", {
        isDraggingRef: isDraggingRef.current,
        isDraggingState: isDragging,
        hasSettleTimer: Boolean(dragSettleTimerRef.current),
        at: Date.now(),
      });
    }
    if (dragSettleTimerRef.current) {
      clearTimeout(dragSettleTimerRef.current);
      dragSettleTimerRef.current = null;
    }
    setHasUserDraggedMap(true);
    isDraggingRef.current = true;
    setIsDragging(true);
    lastMapInteractionAtRef.current = Date.now();
  }, [isDragging]);

  const settleMapDrag = useCallback((source = "unknown") => {
    if (DEBUG_HOME_MAP_SHOVEL) {
      console.log("[home-map-shovel] settle requested", {
        source,
        isDraggingRef: isDraggingRef.current,
        isDraggingState: isDragging,
        hasSettleTimer: Boolean(dragSettleTimerRef.current),
        at: Date.now(),
      });
    }
    updateMapViewportSnapshot();
    if (!isDraggingRef.current) {
      if (DEBUG_HOME_MAP_SHOVEL) {
        console.log("[home-map-shovel] settle skipped; ref is false", {
          source,
          isDraggingState: isDragging,
          at: Date.now(),
        });
      }
      return;
    }

    if (dragSettleTimerRef.current) {
      clearTimeout(dragSettleTimerRef.current);
      dragSettleTimerRef.current = null;
    }

    dragSettleTimerRef.current = setTimeout(() => {
      if (DEBUG_HOME_MAP_SHOVEL) {
        console.log("[home-map-shovel] settle timer fired", {
          source,
          isDraggingRef: isDraggingRef.current,
          isDraggingState: isDragging,
          at: Date.now(),
        });
      }
      const c = mapRef.current?.getCenter();
      if (c) {
        const newC = { lat: c.lat(), lng: c.lng() };
        if (
          !mapCenter ||
          newC.lat !== mapCenter.lat ||
          newC.lng !== mapCenter.lng
        ) {
          onCenterChange(newC);
        }
        const currentZoom = Number(mapRef.current?.getZoom?.());
        const previousZoom = Number(mapViewport?.zoom);
        const zoomedOut =
          Number.isFinite(currentZoom) &&
          Number.isFinite(previousZoom) &&
          currentZoom < previousZoom;
        if (!zoomedOut) {
          preseedQuickFindCategories({
            center: newC,
            trigger: "pan",
          });
        }
      }

      setSelectedPlaceName("");
      setSelectedPlaceAddress("");
      setSelectedPlaceId("");
      setSelectedPlaceCoords(null);
      isDraggingRef.current = false;
      setIsDragging(false);
      if (DEBUG_HOME_MAP_SHOVEL) {
        console.log("[home-map-shovel] dragging reset", {
          source,
          isDraggingRef: isDraggingRef.current,
          at: Date.now(),
        });
      }
      lastMapInteractionAtRef.current = Date.now();
      dragSettleTimerRef.current = null;
    }, 160);
  }, [isDragging, mapCenter, mapViewport?.zoom, onCenterChange, preseedQuickFindCategories, updateMapViewportSnapshot]);

  const handleMapIdle = useCallback(() => {
    settleMapDrag("idle");
  }, [settleMapDrag]);

  const handleMapDragEnd = useCallback(() => {
    settleMapDrag("dragend");
  }, [settleMapDrag]);

  useEffect(() => {
    if (!DEBUG_HOME_MAP_SHOVEL) return;
    console.log("[home-map-shovel] render state", {
      isDragging,
      isDraggingRef: isDraggingRef.current,
      shovel: isDragging ? "hover/wobble" : "dig/static",
      at: Date.now(),
    });
  }, [isDragging]);

  const handleClusterClick = useCallback((groupedMarkers) => {
    setClusterModalUiState(null);
    setFindAnswerText("");
    openClusterFromMap(groupedMarkers);
  }, [openClusterFromMap]);

  const handleDiscoverClick = useCallback(() => {
    clearPendingFindVoiceSubmit();
    setClusterModalUiState(null);
    setFindAnswerText("");
    setFindSearchLocationLabel("");
    setFindDestinationCandidates([]);
    setFindError("");
    setFindVoiceStatus("");
    setLegacyFindHasRecoverableError(false);
    if (geminiFindEnabled) {
      setFindPrompt("");
    } else {
      setUncoverCategories(loadSavedUncoverCategories(currentUserId));
    }
    setUncoverPickerOpen(true);
  }, [clearPendingFindVoiceSubmit, currentUserId, geminiFindEnabled]);

  const handleCloseFindDialog = useCallback(() => {
    if (isUncoverSeeding) return;
    clearPendingFindVoiceSubmit();
    try {
      findRecognitionRef.current?.stop?.();
    } catch {
      // Ignore speech recognition stop errors.
    }
    findRecognitionRef.current = null;
    findVoiceTranscriptRef.current = "";
    setIsFindListening(false);
    setFindDestinationCandidates([]);
    setUncoverPickerOpen(false);
  }, [clearPendingFindVoiceSubmit, isUncoverSeeding]);

  const buildFindResultMarkers = useCallback((capsules = []) => {
    const isSubscribedUser = hasInsiderAccess(subscriptionData, subscriptionTier);
    const center = getUsableMapLatLng(effectiveCenter?.lat, effectiveCenter?.lng);
    return (Array.isArray(capsules) ? capsules : [])
      .map((capsule) =>
        buildHomeMapMarkerFromCapsule(capsule, {
          userId: currentUserId,
          isSubscribedUser,
          usersList: Array.isArray(usersList) ? usersList : [],
          // Every Quick Find capsule carries discoverOnly: true, and the builder
          // drops those by default — without this the whole list silently
          // collapses to empty and the rows lose their signals line and distance.
          includeDiscoverOnly: true,
        })
      )
      .filter(Boolean)
      .map((marker) => ({
        ...marker,
        distanceMeters: center && marker?.position
          ? distanceMeters(center, marker.position)
          : null,
      }));
  }, [currentUserId, effectiveCenter, subscriptionData, subscriptionTier, usersList]);

  const handleToggleLegacyFindCategory = useCallback((key) => {
    const nextKey = String(key || "").trim().toLowerCase();
    if (!nextKey) return;
    setUncoverCategories((prev) => {
      if (prev.includes(nextKey)) {
        return prev.filter((value) => value !== nextKey);
      }
      return [...prev, nextKey];
    });
  }, []);

  const runLegacyFind = useCallback(async ({ skipSeeding = false } = {}) => {
    if (isUncoverSeeding || uncoverCategories.length === 0) return;
    const selectedCategories = normalizeUncoverCategories(uncoverCategories);
    saveUncoverCategoriesPreference(currentUserId, selectedCategories);
    const activeCenter = getUsableMapLatLng(effectiveCenter?.lat, effectiveCenter?.lng);
    setUncoverAnchorCenter(activeCenter);
    if (activeCenter) {
      setHasUserDraggedMap(false);
      onCenterChange(activeCenter);
    }
    setUncoverPickerOpen(false);
    setIsUncoverSeeding(true);
    setFindError("");
    setFindAnswerText("");
    setLegacyFindHasRecoverableError(false);
    const progressId =
      typeof window !== "undefined" &&
      window?.crypto &&
      typeof window.crypto.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `legacy-find-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    try {
      const result = await openUncoverCluster({
        categories: selectedCategories,
        progressId,
        skipSeeding,
        seedCallableName: "legacyFind_seedSponsoredCapsules",
        legacyFindOnly: true,
      });

      if (isLegacyFindNoResults(result)) {
        setFindError(buildLegacyFindNoResultsMessage(selectedCategories));
        setLegacyFindHasRecoverableError(true);
        setUncoverPickerOpen(true);
        return;
      }

      if (result?.seedingError) {
        setFindError("We hit a small bump seeding new capsules, but your nearby uncover is ready.");
      }
    } catch (err) {
      console.error("Legacy Find flow failed:", err);
      setFindError("That uncover attempt did not finish. Try again or open nearby capsules now.");
      setLegacyFindHasRecoverableError(true);
      setUncoverPickerOpen(true);
    } finally {
      setIsUncoverSeeding(false);
      setUncoverAnchorCenter(null);
    }
  }, [currentUserId, effectiveCenter, isUncoverSeeding, onCenterChange, openUncoverCluster, uncoverCategories]);

  const handleRunLegacyFind = useCallback(() => {
    runLegacyFind({ skipSeeding: false });
  }, [runLegacyFind]);

  const handleShowLegacyNearbyNow = useCallback(() => {
    runLegacyFind({ skipSeeding: true });
  }, [runLegacyFind]);

  const submitFindPrompt = useCallback(async (
    value,
    { fromVoice = false, historyPrompt = "", confirmedDestination = null } = {}
  ) => {
    clearPendingFindVoiceSubmit();
    const trimmedPrompt = String(value || "").trim();
    if (!trimmedPrompt || isUncoverSeeding) return;
    const cleanHistoryPrompt = String(historyPrompt || trimmedPrompt).trim();
    const activeCenter = getUsableMapLatLng(effectiveCenter?.lat, effectiveCenter?.lng);
    if (!activeCenter) {
      setFindError("We need a map location before finding nearby capsules.");
      return;
    }

    const findRequestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    recordFindClientEvent("submit:start", {
      findRequestId,
      promptLength: trimmedPrompt.length,
      center: activeCenter,
      fromVoice,
    });
    setFindPrompt(trimmedPrompt);
    setFindError("");
    setFindDestinationCandidates([]);
    if (fromVoice) setFindVoiceStatus(`Got it: "${trimmedPrompt}"`);
    setUncoverPickerOpen(false);
    setUncoverAnchorCenter(activeCenter);
    setFindAdInstanceKey(findRequestId);
    setShowFindAd(true);
    recordFindClientEvent("ad:shown", { findRequestId, timing: "submit:start" });
    setIsUncoverSeeding(true);

    try {
      recordFindClientEvent("callable:before", { findRequestId });
      const result = await askGroundedDiscover({
        prompt: trimmedPrompt,
        center: activeCenter,
        deviceId: getOrCreateUncoverDeviceId(),
        confirmedDestination,
      });
      const resultCapsules = Array.isArray(result?.capsules) ? result.capsules : [];
      const answerText = String(result?.answer || "").trim();
      const searchContext = result?.searchContext || {};
      const resolvedLocationLabel = String(
        searchContext?.resolvedLabel || result?.searchLocationLabel || ""
      ).trim();
      const resultSearchCenter =
        getUsableMapLatLng(result?.searchCenter?.lat, result?.searchCenter?.lng) || activeCenter;
      recordFindClientEvent("callable:after", {
        findRequestId,
        capsuleCount: resultCapsules.length,
        source: result?.source || "",
      });

      if (searchContext?.requiresConfirmation) {
        setFindAnswerText("");
        setFindSearchLocationLabel("");
        setFindDestinationCandidates(Array.isArray(searchContext.candidates) ? searchContext.candidates : []);
        setFindError("Choose which destination you meant.");
        setUncoverPickerOpen(true);
        recordFindClientEvent("destination:confirmation_required", {
          findRequestId,
          candidateCount: Array.isArray(searchContext.candidates) ? searchContext.candidates.length : 0,
        });
        return;
      }

      if (result?.source === "destination_not_found") {
        const destinationError = String(result?.errors?.[0]?.message || "").trim();
        setFindAnswerText("");
        setFindSearchLocationLabel("");
        setFindError(destinationError || "We couldn't find that destination. Try adding a state, province, or country.");
        setUncoverPickerOpen(true);
        recordFindClientEvent("destination:not_found", { findRequestId });
        return;
      }

      setFindOptimisticCapsules(resultCapsules);
      setFindAnswerText(answerText);
      setFindSearchLocationLabel(resolvedLocationLabel);
      // Move the map and re-seed Quick Find at the resolved destination so the category
      // badges (and any subsequent tap) reflect the searched place rather than the user's
      // current location. Gate on a usable search center, not the label, since a remote
      // destination can resolve coordinates without a populated label.
      if (resultSearchCenter) {
        setHasUserDraggedMap(false);
        onCenterChange(resultSearchCenter);
        preseedQuickFindCategories({ center: resultSearchCenter, trigger: "search" });
      }
      setMarkerRefreshKey((prev) => prev + 1);
      const resultMarkers = buildFindResultMarkers(resultCapsules);
      recordFindClientEvent("markers:built", {
        findRequestId,
        markerCount: resultMarkers.length,
      });
      if (resultMarkers.length) {
        if (currentUserId) {
          recordFindHistory(currentUserId, {
            prompt: cleanHistoryPrompt,
            capsules: resultCapsules,
            center: resultSearchCenter,
          }).catch((historyErr) => {
            console.warn("Failed to record Find history:", historyErr);
          });
        }
        openDiscoverFromMarkers(resultMarkers);
        setUncoverPickerOpen(false);
        recordFindClientEvent("results:opened", {
          findRequestId,
          adScheduled: false,
        });
      } else {
        setFindError(
          resolvedLocationLabel
            ? `No pin-ready discoveries came back in ${resolvedLocationLabel}. Try a more specific find.`
            : "No pin-ready discoveries came back yet. Try a more specific nearby find."
        );
        setUncoverPickerOpen(true);
        recordFindClientEvent("results:empty", { findRequestId });
      }
    } catch (err) {
      console.error("Find flow failed:", err);
      recordFindClientEvent("submit:error", {
        findRequestId,
        message: err?.message || String(err),
        code: err?.code || "",
      });
      try {
        Sentry.captureException(err, {
          tags: { feature: "find-ai" },
          extra: { findRequestId, center: activeCenter, promptLength: trimmedPrompt.length },
        });
      } catch {}
      setFindAnswerText("");
      setFindError(err?.message || "Find is temporarily unavailable. Please try again.");
      setUncoverPickerOpen(true);
    } finally {
      setIsUncoverSeeding(false);
      setUncoverAnchorCenter(null);
      recordFindClientEvent("submit:finally", { findRequestId });
    }
  }, [buildFindResultMarkers, clearPendingFindVoiceSubmit, currentUserId, effectiveCenter, isUncoverSeeding, onCenterChange, openDiscoverFromMarkers, preseedQuickFindCategories]);

  const handleFindPromptChange = useCallback((value) => {
    clearPendingFindVoiceSubmit();
    setFindVoiceStatus("");
    setFindDestinationCandidates([]);
    setFindSearchLocationLabel("");
    setFindPrompt(value);
  }, [clearPendingFindVoiceSubmit]);

  const handleFindSubmit = useCallback(async (event) => {
    event?.preventDefault?.();
    clearPendingFindVoiceSubmit();
    setFindVoiceStatus("");
    await submitFindPrompt(findPrompt);
  }, [clearPendingFindVoiceSubmit, findPrompt, submitFindPrompt]);

  const handleConfirmFindDestination = useCallback(async (candidate) => {
    if (!candidate?.center) return;
    setFindDestinationCandidates([]);
    setFindError("");
    setFindSearchLocationLabel(String(candidate?.label || "").trim());
    await submitFindPrompt(findPrompt, { confirmedDestination: candidate });
  }, [findPrompt, submitFindPrompt]);

  const handleQuickFind = useCallback(async (categoryKey) => {
    const key = String(categoryKey || "").trim().toLowerCase();
    if (!QUICK_FIND_CATEGORY_KEYS.includes(key) || isUncoverSeeding) return;
    // Anchor the tap to the center the visible badges were seeded for, not the ambient
    // effectiveCenter (which can lag/diverge from a searched destination and produce
    // stale current-location results). Fall back to effectiveCenter when no seed is active.
    const seededAnchor = quickFindPreseedActiveCenterRef.current;
    const anchorCenter = getUsableMapLatLng(seededAnchor?.center?.lat, seededAnchor?.center?.lng);
    const activeCenter = anchorCenter || getUsableMapLatLng(effectiveCenter?.lat, effectiveCenter?.lng);
    if (!activeCenter) return;
    let cachePlaceId = "";
    let historyLocationLabel = "";
    if (anchorCenter) {
      cachePlaceId = String(seededAnchor?.placeId || "").trim();
      historyLocationLabel =
        cachePlaceId && cachePlaceId === selectedPlaceId
          ? (selectedPlaceName || selectedPlaceAddress)
          : "";
    } else {
      const selectedPlaceMatchesCenter =
        selectedPlaceCoords &&
        distanceMeters(selectedPlaceCoords, activeCenter) <= 120;
      cachePlaceId = selectedPlaceMatchesCenter ? selectedPlaceId : "";
      historyLocationLabel = selectedPlaceMatchesCenter
        ? (selectedPlaceName || selectedPlaceAddress)
        : "";
    }
    const categoryCacheKey = buildQuickFindPreseedCacheKey(activeCenter, key, cachePlaceId);
    const combinedCacheKey = buildQuickFindPreseedCacheKey(activeCenter, QUICK_FIND_PRESEED_KEY, cachePlaceId);
    const cache = quickFindPreseedCacheRef.current || loadQuickFindPreseedCache();
    quickFindPreseedCacheRef.current = cache;

    const openQuickFindCategoryFromCache = (sourceCombinedCacheKey = combinedCacheKey) => {
      const effectiveCategoryCacheKey = sourceCombinedCacheKey === combinedCacheKey
        ? categoryCacheKey
        : String(sourceCombinedCacheKey || "").replace(/:[^:]+$/, `:${key}`);
      const cachedCapsules = resolveQuickFindCachedCapsules({
        cache,
        categoryCacheKey: effectiveCategoryCacheKey,
        ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
      });
      const cachedCapsuleIds = resolveQuickFindCachedCapsuleIds({
        cache,
        categoryCacheKey: effectiveCategoryCacheKey,
        combinedCacheKey: sourceCombinedCacheKey,
        ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
      });
      if (!cachedCapsuleIds.length) return false;
      if (cachedCapsuleIds.length < QUICK_FIND_MIN_READY_COUNT) return false;

      if (cachedCapsules.length) {
        setFindOptimisticCapsules((prev) => mergeCapsulesById(prev, cachedCapsules));
      }
      const cachedIdSet = new Set(cachedCapsuleIds);
      const cachedMarkers = visibleMarkers.filter((marker) =>
        cachedIdSet.has(String(marker?.id || "").trim())
      );
      const optimisticMarkers = buildFindResultMarkers(
        mergeCapsulesById(optimisticPublicCapsules, cachedCapsules)
          .filter((capsule) => cachedIdSet.has(String(capsule?.id || "").trim()))
      );
      const markersById = new Map();
      [...cachedMarkers, ...optimisticMarkers].forEach((marker) => {
        const id = String(marker?.id || "").trim();
        if (id) markersById.set(id, marker);
      });
      const readyMarkers = Array.from(markersById.values()).map((marker) => ({
        ...marker,
        quickFindCategoryKey: key,
      }));
      if (readyMarkers.length) {
        if (currentUserId) {
          const historyCapsuleIdSet = new Set(
            readyMarkers
              .map((marker) => String(marker?.id || "").trim())
              .filter(Boolean)
          );
          const historyRecord = buildQuickFindHistoryRecord({
            categoryKey: key,
            center: activeCenter,
            placeId: cachePlaceId,
            locationLabel: historyLocationLabel,
            markers: readyMarkers,
            capsules: mergeCapsulesById(optimisticPublicCapsules, cachedCapsules)
              .filter((capsule) => historyCapsuleIdSet.has(String(capsule?.id || "").trim())),
          });
          if (historyRecord) {
            recordFindHistory(currentUserId, historyRecord).catch((historyErr) => {
              console.warn("Failed to record Quick Find history:", historyErr);
            });
          }
        }
        clearPendingFindVoiceSubmit();
        setClusterModalUiState(null);
        setFindAnswerText("");
        setFindError("");
        setFindVoiceStatus("");
        setLegacyFindHasRecoverableError(false);
        openDiscoverFromMarkers(readyMarkers);
        return true;
      }
      setMarkerRefreshKey((prev) => prev + 1);
      return false;
    };

    if (openQuickFindCategoryFromCache()) return;
    if (!cachePlaceId) {
      const nearbyCategorySeed = findFreshQuickFindPreseedNearCenter(cache, {
        center: activeCenter,
        categoryKey: key,
        ttlMs: QUICK_FIND_PRESEED_CACHE_TTL_MS,
        radiusMeters: QUICK_FIND_PRESEED_REUSE_RADIUS_METERS,
      });
      if (nearbyCategorySeed?.cacheKey && openQuickFindCategoryFromCache(nearbyCategorySeed.cacheKey)) {
        return;
      }
    }

    clearPendingFindVoiceSubmit();
    setClusterModalUiState(null);
    setFindAnswerText("");
    setFindError("");
    setFindVoiceStatus("");
    setLegacyFindHasRecoverableError(false);
    setActiveQuickFindKey(key);

    try {
      const seedResult = await preseedQuickFindCategories({
        center: activeCenter,
        placeId: cachePlaceId,
        trigger: "tap",
      });
      if (seedResult?.cacheKey) {
        openQuickFindCategoryFromCache(seedResult.cacheKey);
      } else {
        openQuickFindCategoryFromCache();
      }
    } finally {
      setActiveQuickFindKey("");
    }
  }, [
    clearPendingFindVoiceSubmit,
    buildFindResultMarkers,
    currentUserId,
    effectiveCenter,
    isUncoverSeeding,
    openDiscoverFromMarkers,
    optimisticPublicCapsules,
    preseedQuickFindCategories,
    selectedPlaceCoords,
    selectedPlaceAddress,
    selectedPlaceId,
    selectedPlaceName,
    visibleMarkers,
  ]);

  const handleFindVoice = useCallback(() => {
    if (!SpeechRecognition || isUncoverSeeding) return;
    if (isFindListening && findRecognitionRef.current) {
      findRecognitionRef.current.stop();
      setIsFindListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    findVoiceTranscriptRef.current = "";
    setFindPrompt("");
    setFindVoiceStatus("");
    setFindDestinationCandidates([]);
    setFindSearchLocationLabel("");
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      const transcript = String(event.results?.[0]?.[0]?.transcript || "").trim();
      if (transcript) {
        findVoiceTranscriptRef.current = transcript;
        setFindPrompt(transcript);
      }
    };
    recognition.onend = () => {
      setIsFindListening(false);
      const transcript = findVoiceTranscriptRef.current.trim();
      if (transcript) {
        setFindVoiceStatus(`Got it: "${transcript}"`);
        clearPendingFindVoiceSubmit();
        findVoiceSubmitTimerRef.current = setTimeout(() => {
          findVoiceSubmitTimerRef.current = null;
          submitFindPrompt(transcript, { fromVoice: true });
        }, FIND_VOICE_AUTO_SUBMIT_DELAY_MS);
      } else {
        setFindVoiceStatus("I didn't catch that. Try again?");
      }
    };
    recognition.onerror = () => {
      setIsFindListening(false);
      setFindVoiceStatus("I didn't catch that. Try again?");
    };
    findRecognitionRef.current = recognition;
    setIsFindListening(true);
    setFindVoiceStatus("I'm listening...");
    recognition.start();
  }, [SpeechRecognition, clearPendingFindVoiceSubmit, isFindListening, isUncoverSeeding, submitFindPrompt]);

  const handleRefreshUncoverFromModal = useCallback(async () => {
    if (isUncoverSeeding) return;
    const categories = normalizeUncoverCategories(uncoverCategories);
    if (!categories.length) return;
    try {
      await openUncoverCluster({
        categories,
        skipSeeding: true,
      });
    } catch (err) {
      console.warn("Failed to refresh uncover modal:", err);
    }
  }, [isUncoverSeeding, openUncoverCluster, uncoverCategories]);

  const handleDriveHere = useCallback(() => {
    if (!isMobileDevice) return;
    const lat = Number(navigationDestination?.lat);
    const lng = Number(navigationDestination?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    if (/Android/i.test(ua)) {
      const geoUrl = `geo:${lat},${lng}?q=${encodeURIComponent(`${lat},${lng}`)}`;
      window.location.href = geoUrl;
      return;
    }

    if (/iPhone|iPad|iPod/i.test(ua)) {
      const appleUrl = `http://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`;
      window.location.href = appleUrl;
      return;
    }

    const fallbackUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    window.open(fallbackUrl, "_blank", "noopener,noreferrer");
  }, [isMobileDevice, navigationDestination?.lat, navigationDestination?.lng]);


  const mapSearchDisplayText = selectedPlaceName || searchText;
  const { mapRenderCenter } = useMemo(
    () =>
      resolveHomeMapCenterState({
        mapCenter,
        routeMapCenter,
        userLocation,
        defaultCenter: DEFAULT_CENTER,
        permissionState,
        isLocating,
        geoError,
        hasUserDraggedMap,
        isUncoverSeeding,
        uncoverAnchorCenter,
        preferMapCenter: mapCenterSource === "user",
        isResolvingMapCenter: isResolvingInitialCenter,
        waitCapReached: centerWaitCapReached,
      }),
    [
      mapCenter,
      mapCenterSource,
      isResolvingInitialCenter,
      routeMapCenter,
      userLocation,
      permissionState,
      isLocating,
      geoError,
      centerWaitCapReached,
      hasUserDraggedMap,
      isUncoverSeeding,
      uncoverAnchorCenter,
    ]
  );

  // Remove listener on unmount
  useEffect(() => {
    return () => {
      if (dragSettleTimerRef.current) {
        clearTimeout(dragSettleTimerRef.current);
        dragSettleTimerRef.current = null;
      }
      if (pushPrimerTimerRef.current) {
        clearTimeout(pushPrimerTimerRef.current);
        pushPrimerTimerRef.current = null;
      }
      isDraggingRef.current = false;
      if (
        streetViewListenerRef.current &&
        typeof streetViewListenerRef.current.remove === "function"
      ) {
        streetViewListenerRef.current.remove();
      }
      streetViewListenerRef.current = null;
    };
  }, []);

  if (loadError) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Error loading Google Maps</Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", ...containerStyle }}>
      <InstallPrompt
        showPrompt={shouldShowInstallPrompt}
        triggerPrompt={triggerPrompt}
      />

      {successDialogOpen && (
        <SuccessDialog
          sentCapsuleId={successCapsuleId}
          trigger={successDialogOpen}
          payload={successDialogPayload}
          storageKey={successDialogKey}
          onClose={() => {
            const c = mapRef.current?.getCenter();
            if (c) {
              onCenterChange({ lat: c.lat(), lng: c.lng() });
            }
            setSuccessDialogOpen(false);
          }}
        />
      )}

      <Dialog
        open={draftSavedDialogOpen}
        onClose={() => setDraftSavedDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        TransitionComponent={DraftSavedDialogTransition}
      >
        <DialogTitle>Draft saved</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Your draft was saved. You can continue it anytime from Buried drafts.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setDraftSavedDialogOpen(false)}
            sx={(theme) => ({
              fontWeight: 700,
              py: 1.15,
              backgroundColor: theme.colors.blue,
              color: theme.colors.white,
              "&:hover": {
                backgroundColor: theme.colors.blue,
              },
            })}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {geminiFindEnabled ? (
        <FindAskDialog
          open={uncoverPickerOpen}
          prompt={findPrompt}
          voiceStatus={findVoiceStatus}
          error={findError}
          destinationCandidates={findDestinationCandidates}
          isListening={isFindListening}
          isSubmitting={isUncoverSeeding}
          canUseSpeech={canUseFindSpeech}
          onClose={handleCloseFindDialog}
          onPromptChange={handleFindPromptChange}
          onSubmit={handleFindSubmit}
          onVoice={handleFindVoice}
          onConfirmDestination={handleConfirmFindDestination}
        />
      ) : (
        <LegacyFindDialog
          open={uncoverPickerOpen}
          categories={uncoverCategories}
          categoryOptions={UNCOVER_CATEGORY_OPTIONS}
          error={findError}
          hasRecoverableError={legacyFindHasRecoverableError}
          isSubmitting={isUncoverSeeding}
          onClose={handleCloseFindDialog}
          onRun={handleRunLegacyFind}
          onShowNearbyNow={handleShowLegacyNearbyNow}
          onToggleCategory={handleToggleLegacyFindCategory}
        />
      )}

      <PermissionPrimerDialog
        open={permissionDialogOpen}
        onEnable={handleEnableLocationFromPrimer}
        onClose={closePrimer}
      />

      <PushPermissionPrimerDialog
        open={pushPrimerOpen}
        onEnable={requestPushNotifications}
        onClose={closePushPrimer}
      />

      <PermissionDeniedOverlay
        show={deniedOverlayOpen}
        mode="navigation"
        onClose={() => setDeniedOverlayOpen(false)}
      />

      <Dialog
        open={walkRouteError.open}
        onClose={() =>
          setWalkRouteError({ open: false, status: "", destination: null })
        }
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Couldn&apos;t Start Walking Route</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {getWalkRouteErrorMessage(walkRouteError.status)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() =>
              setWalkRouteError({ open: false, status: "", destination: null })
            }
            sx={{ textTransform: "none", borderRadius: "999px" }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const destination = walkRouteError.destination;
              setWalkRouteError({ open: false, status: "", destination: null });
              if (!destination) return;
              runWhenUserLocationReady(() => {
                startWalkRoute(destination);
              });
            }}
            sx={{ textTransform: "none", borderRadius: "999px" }}
          >
            Try again
          </Button>
        </DialogActions>
      </Dialog>

      {!isStreetViewActive && (!isUncoverSeeding || activeQuickFindKey) && (
        <MapControls
          searchText={mapSearchDisplayText}
          onSearchChange={handleSearchChange}
          onSelect={handlePlaceSelect}
          onRecenter={handleRecenterRequest}
          onQuickFind={handleQuickFind}
          activeQuickFindKey={activeQuickFindKey}
          quickFindCategoryStates={quickFindCategoryStates}
          quickFindDisabled={isUncoverSeeding}
          recenterDisabled={false}
        />
      )}

      <RecoverableClusterModalBoundary
        open={isClusterOpen}
        resetKey={[
          clusterMode,
          findAnswerText,
          clusterMarkers.length,
          clusterMarkers.map((marker) => String(marker?.id || "")).join("|"),
        ].join("::")}
        onClose={closeCluster}
        onRetry={() => {
          closeCluster();
          setFindError("We couldn't display those results. Try the search again or adjust the wording.");
          setUncoverPickerOpen(true);
        }}
      >
        <CapsuleClusterModal
          open={isClusterOpen}
          markers={clusterMarkers}
          drafts={clusterMode === "discover" ? [] : nearbyDraftCapsules}
          mode={clusterMode}
          onRefreshUncover={clusterMode === "discover" ? handleRefreshUncoverFromModal : null}
          answerText={clusterMode === "discover" ? findAnswerText : ""}
          locationLabel={clusterMode === "discover" ? findSearchLocationLabel : ""}
          visibilityByKey={HOME_MAP_CATEGORY_FILTERS_ENABLED ? markerVisibility : DEFAULT_MARKER_VISIBILITY}
          onToggleVisibility={handleToggleMarkerVisibility}
          isInsider={isInsider}
          initialUiState={clusterModalUiState}
          onUiStateChange={handleClusterModalUiStateChange}
          bottomReservedSpace={showFindAd && !isStreetViewActive ? FIND_AD_RESERVED_RESULTS_SPACE : "0px"}
          onSelect={(m, options = {}) => {
            handleMarkerClick(m, { fromClusterModal: true, ...options });
          }}
          onSelectDraft={(draft) => {
            handleSelectDraft(draft);
          }}
          onClose={closeCluster}
          onBury={handleConfirmLocation}
          emptyMessage={noCapsulesFoundMessage}
        />
      </RecoverableClusterModalBoundary>

      <HomeMapStage
        mapRenderCenter={mapRenderCenter}
        deferMapRender={deferInitialMapRender}
        userLocation={userLocation}
        displayItems={mapDisplayItems}
        directions={directions}
        onLoad={handleMapLoad}
        onDrag={handleMapDrag}
        onDragEnd={handleMapDragEnd}
        onIdle={handleMapIdle}
        onMarkerClick={handleMarkerClick}
        onClusterClick={handleClusterClick}
        onLongPress={handleMapLongPress}
        onPoiClick={handlePoiClick}
        mapOptions={mapOptions}
        mapContainerStyle={mapContainerStyle}
      />

      <Drawer
        anchor="bottom"
        open={offersSheetOpen}
        onClose={() => setOffersSheetOpen(false)}
        PaperProps={{
          sx: (theme) => ({
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            height: "min(72vh, calc(var(--vh, 1vh) * 72))",
            maxHeight: "calc(var(--vh, 1vh) * 86)",
            bgcolor: `${theme.palette.background.paper} !important`,
            backgroundImage: "none !important",
            color: theme.palette.text.primary,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderTop:
              theme.palette.mode === "dark"
                ? "1px solid rgba(174,194,224,0.22)"
                : `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          }),
        }}
      >
        <Box sx={{ px: 2, pt: 1.25, pb: 0, display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 4,
              borderRadius: 999,
              bgcolor: "text.disabled",
              mx: "auto",
              mb: 1.5,
            }}
          />
          <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 850, lineHeight: 1.1 }}>
                {nearbySheetTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {visibleNearbyOffers.length && visibleNearbyGems.length
                  ? "Coupons, raffles, and recommended capsules around this map area."
                  : visibleNearbyOffers.length
                  ? "Coupons and raffles valid around this map area."
                  : "Recommended time capsules around this map area."}
              </Typography>
            </Box>
            {offersLoading ? <CircularProgress size={22} /> : null}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb: 1.5, overflowX: "auto", pb: 0.25 }}>
            <Chip
              label="All nearby"
              color={activeNearbyShortcut === "all" ? "primary" : "default"}
              variant={activeNearbyShortcut === "all" ? "filled" : "outlined"}
              onClick={() => {
                setActiveNearbyShortcut("all");
                setHighlightedNearbyGemId("");
                nearbySheetScrollRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
              }}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
            {nearbyShortcutState.showFromContacts ? (
              <Chip
                label={getNearbyContactShortcutLabel()}
                color={activeNearbyShortcut === "contacts" ? "primary" : "default"}
                variant={activeNearbyShortcut === "contacts" ? "filled" : "outlined"}
                onClick={() => scrollNearbySheetTo(contactGemRef, "contacts")}
                sx={{ fontWeight: 800, flexShrink: 0 }}
              />
            ) : null}
            {nearbyShortcutState.showLoved ? (
              <Chip
                label={getNearbyLovedShortcutLabel()}
                color={activeNearbyShortcut === "loved" ? "primary" : "default"}
                variant={activeNearbyShortcut === "loved" ? "filled" : "outlined"}
                onClick={() => scrollNearbySheetTo(lovedGemRef, "loved")}
                sx={{ fontWeight: 800, flexShrink: 0 }}
              />
            ) : null}
            {nearbyShortcutState.showMemberExclusives ? (
              <Chip
                label="Member exclusives"
                color={activeNearbyShortcut === "member" ? "primary" : "default"}
                variant={activeNearbyShortcut === "member" ? "filled" : "outlined"}
                onClick={() => scrollNearbySheetTo(memberGemRef, "member")}
                sx={{ fontWeight: 800, flexShrink: 0 }}
              />
            ) : null}
          </Stack>

          {offersError ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {offersError}
            </Typography>
          ) : null}

          <Stack
            spacing={1}
            ref={nearbySheetScrollRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              pr: 0.25,
              minHeight: 0,
              pb: `calc(env(safe-area-inset-bottom, 0px) + ${BOTTOM_NAV_BAR_HEIGHT}px + 16px)`,
            }}
          >
            {visibleNearbyOffers.length ? (
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900, lineHeight: 1.5, flexShrink: 0 }}>
                Offers
              </Typography>
            ) : null}
            {visibleNearbyOffers.map((offer) => {
                const claimPath = getOfferClaimPath(offer);
                const offerCapsuleId = String(
                  offer?.capsule?.id || offer?.capsule?.capsuleId || ""
                ).trim();
                // Capsule-backed offers open the capsule detail (which carries the
                // raffle/coupon CTA) so the funnel runs impression → open → cta_click
                // → claim. Studio offers (no capsule) still deep-link to the claim page.
                const offerTarget = offerCapsuleId
                  ? `/capsule/${encodeURIComponent(offerCapsuleId)}`
                  : claimPath;
                const isCoupon = offer.campaignType === "coupon";
                const offerLabels = buildDiscoveryRowLabels(
                  {
                    capsuleType: offer.capsule?.capsuleType || "",
                    title: offer.title || (isCoupon ? "Coupon offer" : "Raffle entry"),
                    companyName: offer.companyName,
                    locationName: offer.locationName,
                    address: offer.address,
                  },
                  { isInsider }
                );
                const Icon = isCoupon ? LocalOfferRoundedIcon : ConfirmationNumberRoundedIcon;
                return (
                  <CardActionArea
                    key={offer.campaignId}
                    onClick={() => {
                      if (!offerTarget) return;
                      logNearbyOfferOpen(offer);
                      navigateFromNearbySheet(offerTarget);
                    }}
                    sx={{
                      borderRadius: 1,
                      flexShrink: 0,
                      minHeight: 90,
                      border:
                        theme.palette.mode === "dark"
                          ? `1px solid ${alpha(theme.palette.common.white, 0.12)}`
                          : `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                      overflow: "hidden",
                    }}
                  >
                    <Stack direction="row" spacing={1.25} sx={{ p: 1 }}>
                      {offer.heroImageUrl ? (
                        <Box
                          component="img"
                          src={offer.heroImageUrl}
                          alt={offerLabels.title}
                          sx={{ width: 72, height: 72, borderRadius: 1, objectFit: "cover", flexShrink: 0 }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: 1,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            bgcolor: alpha(isCoupon ? theme.palette.success.main : theme.palette.info.main, 0.12),
                            color: isCoupon ? theme.palette.success.main : theme.palette.info.main,
                          }}
                        >
                          <Icon />
                        </Box>
                      )}
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.4, flexWrap: "wrap" }}>
                          <Chip
                            size="small"
                            label={getOfferTypeLabel(offer)}
                            color={isCoupon ? "success" : "info"}
                            sx={{ height: 22, fontWeight: 800 }}
                          />
                          {formatNearbyDistance(offer.distanceMeters, distanceLocale) ? (
                            <Typography variant="caption" color="text.secondary">
                              {formatNearbyDistance(offer.distanceMeters, distanceLocale)}
                            </Typography>
                          ) : null}
                        </Stack>
                        <Typography variant="subtitle2" sx={{ fontWeight: 650, lineHeight: 1.2 }}>
                          {offerLabels.title}
                        </Typography>
                        {offerLabels.secondary ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.35,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                            }}
                          >
                            {offerLabels.secondary}
                          </Typography>
                        ) : null}
                        {offerLabels.tertiary ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              opacity: 0.8,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                            }}
                          >
                            {offerLabels.tertiary}
                          </Typography>
                        ) : null}
                      </Box>
                    </Stack>
                  </CardActionArea>
                );
              })}

            {visibleNearbyGemSections.map((section) => (
              <React.Fragment key={section.key}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.5,
                    pt:
                      visibleNearbyOffers.length ||
                      section.key !== visibleNearbyGemSections[0]?.key
                        ? 1
                        : 0,
                    flexShrink: 0,
                  }}
                >
                  {section.label}
                </Typography>
                {section.items.map((gem) => {
              const isHighlighted = highlightedNearbyGemId === gem.id;
              const isFirstContactGem = gem.id === firstContactGemId;
              const isFirstLovedGem = gem.id === firstLovedGemId;
              const isFirstMemberGem = gem.id === firstMemberGemId;
              const gemKindLabel = getNearbyGemKindLabel(gem);
              const isGatedGem = isGatedMemberExclusive(gem, { isInsider });
              const rowLabels = buildDiscoveryRowLabels(
                {
                  capsuleType: gem.capsuleType,
                  title: gem.title,
                  messageTitle: gem.raw?.messageTitle,
                  seedName: gem.raw?.seedName,
                  companyName: gem.companyName,
                  senderLabel: gem.senderLabel,
                  locationName: gem.placeLabel,
                  address: gem.address,
                  placeSignals: gem.raw?.placeFacts
                    ? {
                        rating: gem.raw.placeFacts.rating ?? null,
                        reviewCount: gem.raw.placeFacts.reviewCount ?? null,
                        priceLevel: gem.raw.placeFacts.priceLevel ?? null,
                      }
                    : null,
                  // Withheld here on purpose: this row already renders its own distance
                  // chip below (formatNearbyDistance(gem.distanceMeters, ...)), so
                  // passing it through to the signals line would show it twice.
                  distanceMeters: null,
                },
                { isInsider, identityInHeader: gem.gemKind === "received", locale: distanceLocale }
              );
              const displayTitle = rowLabels.title;
              const thumbnailUrl = getNearbyGemThumbnailUrl(gem, {
                memberExclusiveFallbackUrl: IconTreasureBoxLocked,
                isGated: isGatedGem,
              });
              const thumbnailFallbackKind = getNearbyGemThumbnailFallbackKind(gem);
              const isFallbackThumbnail = Boolean(thumbnailUrl && thumbnailUrl !== gem.imageUrl);
              return (
                <CardActionArea
                  key={gem.id}
                  ref={
                    isFirstContactGem
                      ? contactGemRef
                      : isFirstLovedGem
                        ? lovedGemRef
                        : isFirstMemberGem
                          ? memberGemRef
                          : null
                  }
                  data-gem-id={gem.id}
                  onClick={() => {
                    logNearbyGemOpen(gem);
                    // A memberExclusive gem must go through the Insider flow for
                    // non-insiders (mirrors the map-marker gate) instead of
                    // opening the capsule directly. Returns the user to this
                    // capsule after free Insider activation via ?returnTo.
                    //
                    // The card still renders gated while the subscription state is
                    // unresolved (withholding the title leaks nothing), but only a
                    // confirmed non-insider gets sent to the upsell — otherwise an
                    // Insider whose profile has not synced yet lands on "Become an
                    // Insider". The capsule page re-checks and gates if needed.
                    if (isGatedGem && subscriptionResolved) {
                      setOffersSheetOpen(false);
                      const returnTo = gem.path;
                      navigate(`/membership?returnTo=${encodeURIComponent(returnTo)}`, {
                        state: { source: "home-gems", returnTo, capsuleId: gem.id },
                      });
                      return;
                    }
                    navigateFromNearbySheet(gem.path);
                  }}
                  sx={{
                    borderRadius: 1,
                    flexShrink: 0,
                    // Sized to the content rather than pinned at 90px: the card
                    // gained a signals line, and a fixed height with overflow
                    // hidden silently clipped the address off the bottom.
                    minHeight: 90,
                    border:
                      theme.palette.mode === "dark"
                        ? `1px solid ${alpha(theme.palette.common.white, isHighlighted ? 0.34 : 0.12)}`
                        : `1px solid ${alpha(theme.palette.text.primary, isHighlighted ? 0.22 : 0.08)}`,
                    overflow: "hidden",
                    boxShadow: isHighlighted ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.22)}` : "none",
                    transition: "box-shadow 180ms ease, border-color 180ms ease",
                  }}
                >
                  <Stack direction="row" spacing={1.25} sx={{ p: 1, height: "100%" }}>
                    {thumbnailUrl ? (
                      isFallbackThumbnail ? (
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: 1,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            overflow: "hidden",
                            bgcolor: alpha(theme.palette.warning.main, 0.08),
                          }}
                        >
                          <Box
                            component="img"
                            src={thumbnailUrl}
                            alt={displayTitle}
                            sx={{ width: 58, height: 58, objectFit: "cover", borderRadius: 1 }}
                          />
                        </Box>
                      ) : (
                        <Box
                          component="img"
                          src={thumbnailUrl}
                          alt={displayTitle}
                          sx={{ width: 72, height: 72, borderRadius: 1, objectFit: "cover", flexShrink: 0 }}
                        />
                      )
                    ) : (
                      <Box
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: 1,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          bgcolor: alpha(theme.palette.warning.main, 0.14),
                          color: theme.palette.warning.main,
                        }}
                      >
                        {thumbnailFallbackKind === "video" ? (
                          <VideocamOutlinedIcon sx={{ fontSize: 40 }} />
                        ) : (
                          <AutoAwesomeRoundedIcon sx={{ fontSize: 58 }} />
                        )}
                      </Box>
                    )}
                    <Box sx={{ minWidth: 0, flex: 1, alignSelf: "center" }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.4, flexWrap: "wrap" }}>
                        {gem.gemKind === "received" ? (
                          <Typography
                            variant="caption"
                            sx={(theme) => ({
                              color: theme.palette.mode === "dark"
                                ? theme.palette.common.white
                                : theme.palette.primary.main,
                              fontWeight: 850,
                              lineHeight: "22px",
                              textShadow: theme.palette.mode === "dark"
                                ? `0 0 10px ${alpha(theme.palette.common.black, 0.45)}`
                                : "none",
                            })}
                          >
                            {gemKindLabel}
                          </Typography>
                        ) : (
                          <Chip
                            size="small"
                            label={gemKindLabel}
                            variant="outlined"
                            sx={getNearbyGemChipSx(gem.gemKind)}
                          />
                        )}
                        {formatNearbyDistance(gem.distanceMeters, distanceLocale) ? (
                          <Typography variant="caption" color="text.secondary">
                            {formatNearbyDistance(gem.distanceMeters, distanceLocale)}
                          </Typography>
                        ) : null}
                      </Stack>
                      <Typography
                        variant="subtitle2"
                        color={gem.gemKind === "received" ? "text.secondary" : "text.primary"}
                        sx={{
                          fontWeight: gem.gemKind === "received" ? 500 : 650,
                          lineHeight: 1.2,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                        }}
                      >
                        {displayTitle}
                      </Typography>
                      {/* Signals sit directly under the title — see the matching
                          comment in CapsuleClusterModal; the two surfaces share the
                          resolver and should share the hierarchy. */}
                      {rowLabels.signals ? (
                        <Typography
                          variant="caption"
                          sx={(theme) => ({
                            color: theme.palette.text.secondary,
                            fontWeight: 700,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            mt: 0.35,
                          })}
                        >
                          {rowLabels.signals}
                        </Typography>
                      ) : null}
                      {rowLabels.secondary ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.25,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          }}
                        >
                          {rowLabels.secondary}
                        </Typography>
                      ) : null}
                      {rowLabels.tertiary ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            opacity: 0.8,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          }}
                        >
                          {rowLabels.tertiary}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                </CardActionArea>
              );
                })}
              </React.Fragment>
            ))}
          </Stack>
        </Box>
      </Drawer>

      <FindLoadingOverlay
        open={isUncoverSeeding && !isStreetViewActive}
        isAndroidChrome={isAndroidChrome}
        isIOSSafari={isIOSSafariBrowser}
        prompt={findPrompt}
        locationLabel={findSearchLocationLabel}
        variant={activeQuickFindKey}
      />

      {showFindAd && !isStreetViewActive ? (
        <VideoAdModal
          key={`find-ad-${findAdInstanceKey}`}
          capsuleType="find"
          capsuleId={`find-${findAdInstanceKey || "active"}`}
          forceShow
          instanceKey={findAdInstanceKey}
          anchoredAboveFooter
          anchorBottom={`calc(env(safe-area-inset-bottom, 0px) + ${BOTTOM_NAV_BAR_HEIGHT}px)`}
          onClose={() => {
            recordFindClientEvent("ad:closed", { findAdInstanceKey });
            setShowFindAd(false);
          }}
        />
      ) : null}

      <MapRefreshOverlay
        open={!isStreetViewActive && !isUncoverSeeding && showMapRefreshOverlay}
        isAndroidChrome={isAndroidChrome}
      />

      {!isStreetViewActive && !isUncoverSeeding && (
        <Box
          component="img"
          src={isDragging ? IconShovelHover : IconShovelDig}
          alt="drop pin"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
            zIndex: 11,
            width: 80,
            height: 80,
            ...(isDragging && {
              transformOrigin: "50% 0%",
              animation: `${wobble} 0.6s infinite ease-in-out`,
            }),
          }}
        />
      )}

      <Dialog open={buryConfirmOpen} onClose={() => setBuryConfirmOpen(false)} fullWidth>
        <DialogTitle>Bury here?</DialogTitle>
        <DialogContent>
          {longPressDraft?.place ? (
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {longPressDraft.place}
            </Typography>
          ) : null}
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {longPressDraft?.address ||
              (longPressDraft
                ? `${longPressDraft.lat.toFixed(5)}, ${longPressDraft.lng.toFixed(5)}`
                : "")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setBuryConfirmOpen(false)}
            sx={(theme) => ({
              textTransform: "none",
              borderRadius: "999px",
              fontWeight: 700,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.mode === "dark"
                ? alpha(theme.palette.text.primary, 0.02)
                : "transparent",
              "&:hover": {
                borderColor: theme.palette.divider,
                backgroundColor: theme.palette.mode === "dark"
                  ? alpha(theme.palette.text.primary, 0.06)
                  : alpha(theme.palette.text.primary, 0.02),
              },
            })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!longPressDraft) return;
              setBuryConfirmOpen(false);
              routeToWizard({
                lat: longPressDraft.lat,
                lng: longPressDraft.lng,
                address: longPressDraft.address || searchText || "",
                place: longPressDraft.place || "",
                placeId: longPressDraft.placeId || "",
              });
            }}
            sx={{ textTransform: "none", borderRadius: "999px" }}
          >
            Bury here
          </Button>
        </DialogActions>
      </Dialog>

      {!isUncoverSeeding && (
        <HomeActionBar
          isNavigating={isNavigating}
          travelTime={travelTime}
          showDriveButton={isMobileDevice && Boolean(navigationDestination)}
          isStreetViewActive={isStreetViewActive}
          nearbyOffersCount={visibleNearbyOffers.length}
          nearbyGemsCount={visibleNearbyGems.length}
          nearbyDiscoveryDisplayCount={nearbyDiscoveryDisplayCount}
          onDiscover={handleDiscoverClick}
          onNearbyDiscovery={() => setOffersSheetOpen(true)}
          onBury={handleConfirmLocation}
          onCancelNavigation={clearNavigation}
          onDriveHere={handleDriveHere}
        />
      )}
    </Box>
  );
}
